import React, { useState, useEffect } from 'react';
import { Shield, Users, FileText, TrendingUp, AlertTriangle, Target, BookOpen, CheckCircle, XCircle, ChevronDown, ChevronUp, BarChart3, Calendar, DollarSign, Scale, Briefcase, Award, GitBranch, MessageSquare, Download, ClipboardCheck, ExternalLink, Rocket, X, Copy, UserPlus } from 'lucide-react';
import WelcomeModal from './WelcomeModal';

const BoardOfSecurity = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedFramework, setSelectedFramework] = useState('nist');
  const [reportingFrequency, setReportingFrequency] = useState('quarterly');
  const [organizationalLevel, setOrganizationalLevel] = useState('');
  const [boardMaturity, setBoardMaturity] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [showAssessmentResults, setShowAssessmentResults] = useState(false);
  // CISO Marketplace central gate: the assessment is gated UP FRONT (first answer → requireRun).
  const [csmStarted, setCsmStarted] = useState(false);
  const ensureStarted = async () => {
    if (csmStarted) return true;
    const g = window.csmGate;
    const ok = g?.requireRun ? await g.requireRun() : true; // graceful: no gate → allow
    if (ok) setCsmStarted(true);
    return ok;
  };
  useEffect(() => {
    try { const s = localStorage.getItem('cyberboard_answers'); if (s) setAssessmentAnswers(JSON.parse(s)); } catch (e) { /* ignore */ }
    const g = window.csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && g?.requireRun) {
      g.requireRun().then((ok) => { if (ok) setCsmStarted(true); });
    }
  }, []);
  useEffect(() => {
    try { localStorage.setItem('cyberboard_answers', JSON.stringify(assessmentAnswers)); } catch (e) { /* ignore */ }
  }, [assessmentAnswers]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Tab Navigation
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'assessment', label: 'Maturity Assessment', icon: CheckCircle },
    { id: 'reporting', label: 'Board Reporting', icon: FileText },
    { id: 'metrics', label: 'Metrics & KPIs', icon: BarChart3 },
    { id: 'structure', label: 'Org Structure', icon: GitBranch },
    { id: 'compliance', label: 'SEC Compliance', icon: Scale },
    { id: 'frameworks', label: 'Global Frameworks', icon: Award },
    { id: 'engagement', label: 'Board Engagement', icon: MessageSquare },
    { id: 'prep', label: 'Meeting Prep Wizard', icon: ClipboardCheck },
    { id: 'templates', label: 'Templates', icon: Download }
  ];

  // Overview Tab Content
  const OverviewTab = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8 rounded-lg">
        <h2 className="text-3xl font-bold mb-4">Board of Security Governance</h2>
        <p className="text-lg mb-6">
          Comprehensive toolkit for CISO-Board alignment, reporting excellence, and strategic security governance
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
            <div className="text-3xl font-bold">Only 36%</div>
            <div className="text-sm">of CISOs report having adequate budget for their security program (Gartner, 2025)</div>
          </div>
          <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
            <div className="text-3xl font-bold">4 Business Days</div>
            <div className="text-sm">SEC material cybersecurity incident disclosure deadline (Form 8-K)</div>
          </div>
          <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
            <div className="text-3xl font-bold">27%</div>
            <div className="text-sm">of CISOs now report directly to the CEO — up from 3% in 2020 (IANS, 2025)</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white/90 p-6 rounded-lg shadow-sm border-l-4 border-red-500">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-500 mt-1" size={24} />
            <div>
              <h3 className="text-xl font-bold mb-2 text-gray-800">Key Challenges</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Budget constraints:</strong> Only 36% of CISOs report having adequate budget for their security program (Gartner, 2025)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Reporting hierarchy:</strong> 47% of CISOs still report to the CIO, limiting independence and board access (IANS, 2025)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>AI threat escalation:</strong> AI-powered attacks are accelerating threat velocity faster than most security programs can adapt</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Technical translation:</strong> Board members still struggle to connect cybersecurity risk to business outcomes and financial exposure</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Regulatory pressure:</strong> Cascading requirements (SEC, DORA, NIS2, DPDP) are straining governance and disclosure processes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white/90 p-6 rounded-lg shadow-sm border-l-4 border-green-500">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 mt-1" size={24} />
            <div>
              <h3 className="text-xl font-bold mb-2 text-gray-800">Best Practices</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span><strong>Direct CEO reporting:</strong> Enhanced visibility and strategic alignment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span><strong>Business language:</strong> Focus on risk, revenue, and business impact</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span><strong>Quarterly cadence:</strong> Regular board meetings with monthly risk committee</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span><strong>Board education:</strong> Tabletop exercises and security awareness training</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span><strong>Risk quantification:</strong> Financial impact metrics that boards understand</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h3 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
          <BookOpen size={24} className="text-blue-600" />
          Why This Matters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
          <div>
            <h4 className="font-semibold mb-2">For CISOs:</h4>
            <ul className="space-y-1 text-sm">
              <li>• Build credibility and influence at executive level</li>
              <li>• Secure budget approval for critical initiatives</li>
              <li>• Demonstrate value and ROI of security programs</li>
              <li>• Navigate organizational politics effectively</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">For Boards:</h4>
            <ul className="space-y-1 text-sm">
              <li>• Meet SEC cybersecurity disclosure requirements</li>
              <li>• Understand cyber risk in business terms</li>
              <li>• Make informed decisions on security investments</li>
              <li>• Fulfill fiduciary duties for cyber oversight</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Board Maturity Assessment Tab
  const AssessmentTab = () => {
    const questions = [
      {
        id: 'q1',
        question: 'How often does your board receive cybersecurity updates?',
        options: [
          { value: 0, label: 'Only when incidents occur' },
          { value: 1, label: 'Annually' },
          { value: 2, label: 'Semi-annually' },
          { value: 3, label: 'Quarterly or more frequently' }
        ]
      },
      {
        id: 'q2',
        question: 'Does at least one board member have cybersecurity expertise?',
        options: [
          { value: 0, label: 'No cybersecurity expertise on board' },
          { value: 1, label: 'Limited technical background' },
          { value: 2, label: 'One member with some cyber experience' },
          { value: 3, label: 'Multiple members with strong cyber expertise' }
        ]
      },
      {
        id: 'q3',
        question: 'How does your CISO report to the board?',
        options: [
          { value: 0, label: 'No direct board reporting' },
          { value: 1, label: 'Through CIO or other executive' },
          { value: 2, label: 'Direct quarterly updates' },
          { value: 3, label: 'Direct reporting with board-level access' }
        ]
      },
      {
        id: 'q4',
        question: 'Does your board review cybersecurity metrics and KPIs?',
        options: [
          { value: 0, label: 'No formal metrics reviewed' },
          { value: 1, label: 'Ad-hoc metrics when requested' },
          { value: 2, label: 'Standard metrics reported quarterly' },
          { value: 3, label: 'Comprehensive dashboard with trend analysis' }
        ]
      },
      {
        id: 'q5',
        question: 'How does your board oversee cybersecurity budget?',
        options: [
          { value: 0, label: 'No visibility into security budget' },
          { value: 1, label: 'Bundled with IT budget' },
          { value: 2, label: 'Separate line item reviewed annually' },
          { value: 3, label: 'Strategic budget planning with ROI analysis' }
        ]
      },
      {
        id: 'q6',
        question: 'Has your board participated in cybersecurity tabletop exercises?',
        options: [
          { value: 0, label: 'Never' },
          { value: 1, label: 'Once, several years ago' },
          { value: 2, label: 'Occasionally (every 2-3 years)' },
          { value: 3, label: 'Annually or more frequently' }
        ]
      },
      {
        id: 'q7',
        question: 'Does your board have a documented incident response escalation process?',
        options: [
          { value: 0, label: 'No formal process' },
          { value: 1, label: 'Informal understanding' },
          { value: 2, label: 'Documented but not tested' },
          { value: 3, label: 'Documented, tested, and regularly updated' }
        ]
      },
      {
        id: 'q8',
        question: 'How does your board oversee third-party risk?',
        options: [
          { value: 0, label: 'No board-level oversight' },
          { value: 1, label: 'Aware of major vendors only' },
          { value: 2, label: 'Reviews vendor risk assessments' },
          { value: 3, label: 'Active governance with risk thresholds' }
        ]
      },
      {
        id: 'q9',
        question: 'Is your board familiar with SEC cybersecurity disclosure requirements?',
        options: [
          { value: 0, label: 'Not familiar' },
          { value: 1, label: 'Basic awareness' },
          { value: 2, label: 'Good understanding of requirements' },
          { value: 3, label: 'Fully compliant with regular reviews' }
        ]
      },
      {
        id: 'q10',
        question: 'Does your board evaluate the CISO\'s performance?',
        options: [
          { value: 0, label: 'No formal evaluation' },
          { value: 1, label: 'Through CIO evaluation only' },
          { value: 2, label: 'Annual review with some input' },
          { value: 3, label: 'Comprehensive evaluation with clear KPIs' }
        ]
      }
    ];

    const handleAnswerChange = async (questionId, value) => {
      if (!(await ensureStarted())) return; // gate overlay shown — ignore until entitled
      setAssessmentAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    };

    const calculateScore = () => {
      const totalQuestions = questions.length;
      const answeredQuestions = Object.keys(assessmentAnswers).length;

      if (answeredQuestions < totalQuestions) {
        alert(`Please answer all ${totalQuestions} questions to see your results.`);
        return;
      }

      setShowAssessmentResults(true);
      // Save to the member portal's Reports tab (logged-in only).
      const g = window.csmGate;
      if (g?.isLoggedIn?.() && g.saveReport) {
        const sc = Object.values(assessmentAnswers).reduce((s, v) => s + v, 0);
        g.saveReport({ title: 'CyberBoard Report', summary: 'board security oversight maturity — ' + sc + '/30', data: { assessmentAnswers, score: sc } });
      }
    };

    const getScore = () => {
      return Object.values(assessmentAnswers).reduce((sum, val) => sum + val, 0);
    };

    const getMaturityLevel = (score) => {
      if (score <= 7) return { level: 'Beginner', color: 'red', description: 'Your board is just starting its cybersecurity oversight journey.' };
      if (score <= 15) return { level: 'Developing', color: 'orange', description: 'Your board has basic cybersecurity oversight in place but needs improvement.' };
      if (score <= 23) return { level: 'Mature', color: 'blue', description: 'Your board demonstrates strong cybersecurity governance practices.' };
      return { level: 'Leading', color: 'green', description: 'Your board is a leader in cybersecurity oversight and governance.' };
    };

    const resetAssessment = () => {
      setAssessmentAnswers({});
      setShowAssessmentResults(false);
    };

    const score = getScore();
    const maturity = getMaturityLevel(score);

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-lg">
          <h2 className="text-3xl font-bold mb-4">Board Cyber Maturity Assessment</h2>
          <p className="text-lg">
            Take this 10-question assessment to evaluate your board's cybersecurity oversight maturity. Get instant results with actionable recommendations.
          </p>
        </div>

        {!showAssessmentResults ? (
          <div className="bg-white/90 p-6 rounded-lg shadow-sm">
            <div className="space-y-6">
              {questions.map((q, index) => (
                <div key={q.id} className="border-b pb-6 last:border-b-0">
                  <h3 className="font-bold text-lg mb-3 text-gray-800">
                    {index + 1}. {q.question}
                  </h3>
                  <div className="space-y-2 ml-4">
                    {q.options.map((option) => (
                      <label key={option.value} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="radio"
                          name={q.id}
                          value={option.value}
                          checked={assessmentAnswers[q.id] === option.value}
                          onChange={() => handleAnswerChange(q.id, option.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                onClick={calculateScore}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <CheckCircle size={20} />
                Calculate My Score
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className={`bg-${maturity.color}-50 border-2 border-${maturity.color}-200 p-8 rounded-lg`}>
              <div className="text-center mb-6">
                <div className={`text-6xl font-bold text-${maturity.color}-600 mb-2`}>
                  {score} / 30
                </div>
                <div className={`text-3xl font-bold text-${maturity.color}-700 mb-2`}>
                  {maturity.level}
                </div>
                <p className="text-lg text-gray-700">{maturity.description}</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-xl mb-4 text-gray-800">Your Maturity Breakdown</h3>
                <div className="space-y-4">
                  {questions.map((q, index) => {
                    const answer = assessmentAnswers[q.id];
                    const answeredOption = q.options.find(opt => opt.value === answer);
                    return (
                      <div key={q.id} className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{index + 1}. {q.question}</p>
                          <p className="text-sm text-gray-600 mt-1">{answeredOption?.label}</p>
                        </div>
                        <div className={`ml-4 px-3 py-1 rounded font-bold ${
                          answer === 3 ? 'bg-green-100 text-green-700' :
                          answer === 2 ? 'bg-blue-100 text-blue-700' :
                          answer === 1 ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {answer} / 3
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="bg-white/90 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-xl mb-4 text-gray-800 flex items-center gap-2">
                <Rocket size={24} className="text-blue-600" />
                Recommendations for {maturity.level} Boards
              </h3>
              <div className="space-y-4">
                {maturity.level === 'Beginner' && (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Establish Regular Reporting</p>
                        <p className="text-sm text-gray-600">Set up quarterly cybersecurity updates from your CISO to the board.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Build Board Expertise</p>
                        <p className="text-sm text-gray-600">Recruit at least one board member with cybersecurity background.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Define Basic Metrics</p>
                        <p className="text-sm text-gray-600">Identify 5-7 key cybersecurity metrics to track consistently.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Create Incident Escalation Plan</p>
                        <p className="text-sm text-gray-600">Document when and how cybersecurity incidents should reach the board.</p>
                      </div>
                    </div>
                  </>
                )}

                {maturity.level === 'Developing' && (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Enhance CISO Access</p>
                        <p className="text-sm text-gray-600">Give your CISO direct board access, not filtered through CIO.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Run Tabletop Exercise</p>
                        <p className="text-sm text-gray-600">Conduct your first board-level cybersecurity incident simulation.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Separate Security Budget</p>
                        <p className="text-sm text-gray-600">Break out cybersecurity spending from IT budget for better visibility.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Review SEC Compliance</p>
                        <p className="text-sm text-gray-600">Ensure board understands SEC cybersecurity disclosure requirements.</p>
                      </div>
                    </div>
                  </>
                )}

                {maturity.level === 'Mature' && (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Implement Advanced KPIs</p>
                        <p className="text-sm text-gray-600">Add risk quantification and ROI metrics to your dashboard.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Formalize CISO Evaluation</p>
                        <p className="text-sm text-gray-600">Create comprehensive performance review process with board input.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Regular Tabletop Exercises</p>
                        <p className="text-sm text-gray-600">Move to annual or bi-annual board-level incident simulations.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Third-Party Risk Governance</p>
                        <p className="text-sm text-gray-600">Establish board-level thresholds for vendor risk acceptance.</p>
                      </div>
                    </div>
                  </>
                )}

                {maturity.level === 'Leading' && (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Share Best Practices</p>
                        <p className="text-sm text-gray-600">Consider publishing your governance framework to help other boards.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Mentor Other Boards</p>
                        <p className="text-sm text-gray-600">Offer to share lessons learned with peer organizations.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Continuous Improvement</p>
                        <p className="text-sm text-gray-600">Regularly benchmark against emerging standards and frameworks.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={20} />
                      <div>
                        <p className="font-semibold">Innovate Governance Models</p>
                        <p className="text-sm text-gray-600">Explore AI/ML for board-level risk intelligence and automation.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-bold text-lg mb-3 text-gray-800">Next Steps</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Use the <strong>Templates</strong> tab to download governance documents</li>
                <li>• Try the <strong>Meeting Prep Wizard</strong> to prepare for your next board meeting</li>
                <li>• Review the <strong>Board Reporting</strong> tab for best practices</li>
                <li>• Explore our other tools: <a href="https://cisobudgetbuilder.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">CISO Budget Builder</a>, <a href="https://incidentresponse.tools" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Incident Response Tools</a></li>
              </ul>
              <button
                onClick={resetAssessment}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retake Assessment
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Board Reporting Tab
  const ReportingTab = () => (
    <div className="space-y-6">
      <div className="bg-white/90 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Board Reporting Framework</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Select Reporting Frequency:</label>
          <select 
            value={reportingFrequency}
            onChange={(e) => setReportingFrequency(e.target.value)}
            className="w-full md:w-64 p-2 border rounded-lg"
          >
            <option value="monthly">Monthly (Risk Committee)</option>
            <option value="quarterly">Quarterly (Board Meeting)</option>
            <option value="annual">Annual (10-K Filing)</option>
          </select>
        </div>

        <div className="space-y-4">
          {/* Executive Summary */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('execSummary')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>1. Executive Summary</span>
              {expandedSections.execSummary ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.execSummary && (
              <div className="p-4 bg-slate-50">
                <p className="text-gray-700 mb-3">1-2 paragraph concise takeaway that directly answers the question "Are we secure?"</p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Template:</h4>
                  <p className="text-sm italic text-gray-700">
                    "Our cybersecurity posture remains [strong/improving/at-risk]. This quarter, we [key achievement]. 
                    Critical focus areas include [top 3 priorities]. Budget request of [amount] addresses [specific risk gaps] 
                    with projected [X%] risk reduction and [ROI metric]."
                  </p>
                </div>
                <div className="mt-3 space-y-2">
                  <p className="font-semibold text-sm">Key Elements:</p>
                  <ul className="text-sm space-y-1 text-gray-700">
                    <li>• Overall security posture (Red/Yellow/Green status)</li>
                    <li>• Quarter's most significant achievement or incident</li>
                    <li>• Top 3 priorities for next period</li>
                    <li>• Budget requests tied to business risk</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Cybersecurity Status */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('cyberStatus')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>2. Current Cybersecurity Status & Incidents</span>
              {expandedSections.cyberStatus ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.cyberStatus && (
              <div className="p-4 bg-white space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">What to Include:</h4>
                  <ul className="text-sm space-y-2 text-gray-700">
                    <li>• <strong>Recent incidents:</strong> Material events, business impact, remediation status</li>
                    <li>• <strong>Near-misses:</strong> Attacks blocked, threats detected and neutralized</li>
                    <li>• <strong>Response effectiveness:</strong> MTTD, MTTR, and trend analysis</li>
                    <li>• <strong>Security initiatives:</strong> Progress on roadmap items (zero trust, cloud security)</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-sm font-semibold mb-1">⚠️ What NOT to Include:</p>
                  <p className="text-sm text-gray-700">Avoid technical jargon, raw alert volumes, or tool-specific metrics. Focus on business outcomes.</p>
                </div>
              </div>
            )}
          </div>

          {/* Threat Landscape */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('threatLandscape')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>3. Threat Landscape & Industry Context</span>
              {expandedSections.threatLandscape ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.threatLandscape && (
              <div className="p-4 bg-slate-50">
                <ul className="text-sm space-y-2 text-gray-700">
                  <li>• <strong>Industry-specific threats:</strong> Relevant to your sector (ransomware, supply chain, operational technology)</li>
                  <li>• <strong>AI-powered attacks (2026 priority):</strong> Adversarial AI is enabling faster phishing, deepfake fraud, and automated vulnerability exploitation at a scale and velocity that outpaces traditional defenses</li>
                  <li>• <strong>Geopolitical considerations:</strong> Nation-state actors, critical infrastructure targeting, and sanctions-related cyber activity</li>
                  <li>• <strong>Competitor incidents:</strong> Lessons learned from industry breaches and regulatory actions</li>
                  <li>• <strong>Our preparedness:</strong> How we're positioned against these threats relative to our peers</li>
                </ul>
              </div>
            )}
          </div>

          {/* Risk Management */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('riskMgmt')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>4. Risk Management & Mitigation Strategy</span>
              {expandedSections.riskMgmt ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.riskMgmt && (
              <div className="p-4 bg-white space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Risk-Based Approach:</h4>
                  <ul className="text-sm space-y-2 text-gray-700">
                    <li>• <strong>Critical asset inventory:</strong> Crown jewels and their protection status</li>
                    <li>• <strong>Top 5 risks:</strong> Ranked by business impact, with mitigation plans</li>
                    <li>• <strong>Residual risk:</strong> Accepted risks and why</li>
                    <li>• <strong>Risk appetite alignment:</strong> How current posture matches board's tolerance</li>
                    <li>• <strong>Quantified exposure:</strong> Financial impact of potential incidents</li>
                  </ul>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm font-semibold mb-1">✓ Best Practice:</p>
                  <p className="text-sm text-gray-700">
                    Use heat maps to visualize risk severity vs likelihood. Show trend arrows indicating improving/worsening conditions.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Compliance & Regulatory */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('compliance')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>5. Compliance & Regulatory Updates</span>
              {expandedSections.compliance ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.compliance && (
              <div className="p-4 bg-slate-50">
                <ul className="text-sm space-y-2 text-gray-700">
                  <li>• <strong>SEC requirements:</strong> Disclosure readiness, 4-day incident reporting capability</li>
                  <li>• <strong>Framework alignment:</strong> NIST CSF, ISO 27001, SOC 2, GDPR status</li>
                  <li>• <strong>Audit findings:</strong> Internal/external audit results and remediation</li>
                  <li>• <strong>Regulatory changes:</strong> Upcoming requirements and preparation status</li>
                  <li>• <strong>Compliance gaps:</strong> Areas needing attention and investment</li>
                </ul>
              </div>
            )}
          </div>

          {/* Financial Impact */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('financial')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>6. Financial Impact & Budget Requests</span>
              {expandedSections.financial ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.financial && (
              <div className="p-4 bg-white space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Financial Reporting Elements:</h4>
                  <ul className="text-sm space-y-2 text-gray-700">
                    <li>• <strong>Budget utilization:</strong> Current spend vs allocated budget</li>
                    <li>• <strong>Cost of incidents:</strong> Financial losses from breaches/downtime</li>
                    <li>• <strong>ROI of security:</strong> Attacks prevented, value protected</li>
                    <li>• <strong>Insurance coverage:</strong> Cyber insurance adequacy and claims</li>
                    <li>• <strong>Budget requests:</strong> New investments with clear business justification</li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Budget Request Template:</h4>
                  <p className="text-sm italic text-gray-700">
                    "Requesting $[amount] for [initiative]. Current gap: [risk description]. 
                    If funded: [X%] reduction in exposure to [business risk]. Payback period: [timeframe]. 
                    Without this: [quantified business impact]."
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Third-Party Risk */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('thirdParty')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>7. Third-Party & Supply Chain Risk</span>
              {expandedSections.thirdParty ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.thirdParty && (
              <div className="p-4 bg-slate-50">
                <ul className="text-sm space-y-2 text-gray-700">
                  <li>• <strong>Vendor risk assessment:</strong> Critical vendors and their security posture</li>
                  <li>• <strong>Third-party incidents:</strong> Vendor breaches impacting us</li>
                  <li>• <strong>Due diligence process:</strong> How we vet and monitor vendors</li>
                  <li>• <strong>Contract requirements:</strong> Security clauses and breach notification terms</li>
                  <li>• <strong>Supply chain vulnerabilities:</strong> Dependencies and concentration risks</li>
                </ul>
              </div>
            )}
          </div>

          {/* People & Culture */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('people')}
              className="w-full bg-gray-100 p-4 text-left font-semibold flex justify-between items-center hover:bg-gray-200"
            >
              <span>8. People, Training & Security Culture</span>
              {expandedSections.people ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.people && (
              <div className="p-4 bg-slate-50">
                <ul className="text-sm space-y-2 text-gray-700">
                  <li>• <strong>Security awareness:</strong> Training completion rates, phishing test results</li>
                  <li>• <strong>Team capacity:</strong> Staffing levels, open positions, retention</li>
                  <li>• <strong>Skills development:</strong> Certifications, training investments</li>
                  <li>• <strong>Board education:</strong> Tabletop exercises, security briefings</li>
                  <li>• <strong>Security champions:</strong> Business unit engagement and advocacy</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <AlertTriangle className="text-yellow-600" size={20} />
          Common Reporting Mistakes to Avoid
        </h3>
        <ul className="text-sm space-y-1 text-gray-700">
          <li>❌ Using technical jargon (firewall rules, SIEM alerts, CVE numbers)</li>
          <li>❌ Reporting only problems without solutions or recommendations</li>
          <li>❌ Overwhelming with too many metrics that don't tell a story</li>
          <li>❌ Focusing on effort/activity instead of outcomes/results</li>
          <li>❌ Not connecting security initiatives to business objectives</li>
          <li>❌ Failing to quantify financial impact of risks and recommendations</li>
        </ul>
      </div>
    </div>
  );

  // Metrics & KPIs Tab
  const MetricsTab = () => (
    <div className="space-y-6">
      <div className="bg-white/90 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Cybersecurity Metrics & KPIs</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Select Framework:</label>
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="w-full md:w-64 p-2 border rounded-lg"
          >
            <option value="nist">NIST Cybersecurity Framework</option>
            <option value="iso">ISO 27001</option>
            <option value="cis">CIS Controls</option>
            <option value="custom">Custom/Hybrid</option>
          </select>
        </div>

        {/* Framework Context Box */}
        {selectedFramework === 'nist' && (
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6 rounded">
            <h3 className="font-bold text-blue-800 mb-2">NIST Cybersecurity Framework 2.0 Alignment</h3>
            <p className="text-sm text-gray-700">
              These metrics align with the six NIST CSF 2.0 functions: Govern, Identify, Protect, Detect, Respond, and Recover. The new Govern function (added in 2024) specifically addresses board-level oversight, making CSF 2.0 the most board-relevant version to date.
            </p>
          </div>
        )}

        {selectedFramework === 'iso' && (
          <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-6 rounded">
            <h3 className="font-bold text-purple-800 mb-2">ISO 27001 Alignment</h3>
            <p className="text-sm text-gray-700">
              These metrics support ISO 27001 Annex A controls and demonstrate ISMS effectiveness. Emphasize control implementation rates, audit findings, and risk treatment progress for certification and continuous improvement.
            </p>
          </div>
        )}

        {selectedFramework === 'cis' && (
          <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mb-6 rounded">
            <h3 className="font-bold text-orange-800 mb-2">CIS Controls Alignment</h3>
            <p className="text-sm text-gray-700">
              These metrics map to CIS Critical Security Controls (Implementation Groups 1-3). Focus on vulnerability management, secure configuration, and continuous monitoring metrics that demonstrate control implementation maturity.
            </p>
          </div>
        )}

        {selectedFramework === 'custom' && (
          <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-6 rounded">
            <h3 className="font-bold text-green-800 mb-2">Custom/Hybrid Framework</h3>
            <p className="text-sm text-gray-700">
              These metrics are framework-agnostic and can support any combination of NIST, ISO, CIS, or industry-specific requirements. Select the most relevant metrics for your board's priorities and organizational risk appetite.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Strategic/Board-Level Metrics */}
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 text-lg flex items-center gap-2">
              <Target className="text-blue-600" size={20} />
              Strategic (Board-Level)
            </h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Quantified Risk Exposure (QRE)</h4>
                <p className="text-xs text-gray-600 mb-1">Financial impact of current risk posture</p>
                <p className="text-xs italic">Example: "$2.5M potential annual loss exposure from top 5 risks"</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Security Posture Score</h4>
                <p className="text-xs text-gray-600 mb-1">Overall security health rating (0-100)</p>
                <p className="text-xs italic">Example: "Score improved from 72 to 78 this quarter"</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Breach Probability & Impact</h4>
                <p className="text-xs text-gray-600 mb-1">Likelihood x financial consequence</p>
                <p className="text-xs italic">Example: "15% probability, $9.8M avg breach cost in our industry (IBM, 2025 — US average)"</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Security ROI</h4>
                <p className="text-xs text-gray-600 mb-1">Value delivered vs investment</p>
                <p className="text-xs italic">Example: "$5M invested prevented $18M in potential losses"</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Cyber Insurance Coverage Ratio</h4>
                <p className="text-xs text-gray-600 mb-1">Coverage vs potential exposure</p>
                <p className="text-xs italic">Example: "80% of QRE covered by cyber insurance"</p>
              </div>
            </div>
          </div>

          {/* Operational Metrics */}
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 text-lg flex items-center gap-2">
              <BarChart3 className="text-green-600" size={20} />
              Operational (CISO-Level)
            </h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Mean Time to Detect (MTTD)</h4>
                <p className="text-xs text-gray-600 mb-1">How quickly threats are identified</p>
                <p className="text-xs italic">Example: "MTTD reduced from 21 days to 8 days"</p>
              </div>
              
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Mean Time to Respond (MTTR)</h4>
                <p className="text-xs text-gray-600 mb-1">Speed of incident containment</p>
                <p className="text-xs italic">Example: "MTTR: 4 hours (industry avg: 12 hours)"</p>
              </div>
              
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Critical Vulnerability Remediation</h4>
                <p className="text-xs text-gray-600 mb-1">High/critical vulns patched within SLA</p>
                <p className="text-xs italic">Example: "92% of critical vulns patched within 7 days"</p>
              </div>
              
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Security Incident Rate</h4>
                <p className="text-xs text-gray-600 mb-1">Number and severity of incidents</p>
                <p className="text-xs italic">Example: "3 medium incidents, 0 high/critical this quarter"</p>
              </div>
              
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Phishing Click Rate</h4>
                <p className="text-xs text-gray-600 mb-1">User awareness effectiveness</p>
                <p className="text-xs italic">Example: "Click rate: 8% (down from 22% last year)"</p>
              </div>
            </div>
          </div>

          {/* Risk-Based Metrics */}
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 text-lg flex items-center gap-2">
              <AlertTriangle className="text-orange-600" size={20} />
              Risk-Based (Forward-Looking)
            </h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Unpatched Critical Systems</h4>
                <p className="text-xs text-gray-600 mb-1">High-risk exposure count</p>
                <p className="text-xs italic">Example: "12 internet-facing systems require patches"</p>
              </div>
              
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Third-Party Risk Score</h4>
                <p className="text-xs text-gray-600 mb-1">Vendor security posture</p>
                <p className="text-xs italic">Example: "2 critical vendors below acceptable threshold"</p>
              </div>
              
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Data Exposure Risk</h4>
                <p className="text-xs text-gray-600 mb-1">Sensitive data at risk</p>
                <p className="text-xs italic">Example: "2.3M customer records in systems with gaps"</p>
              </div>
              
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Attack Surface Growth Rate</h4>
                <p className="text-xs text-gray-600 mb-1">Expansion of exposed assets</p>
                <p className="text-xs italic">Example: "Cloud footprint grew 30%, controls lagging"</p>
              </div>
              
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Privileged Access Violations</h4>
                <p className="text-xs text-gray-600 mb-1">Admin account misuse</p>
                <p className="text-xs italic">Example: "5 violations this month, 2 under investigation"</p>
              </div>
            </div>
          </div>

          {/* Compliance Metrics */}
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 text-lg flex items-center gap-2">
              <CheckCircle className="text-purple-600" size={20} />
              Compliance & Governance
            </h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Control Effectiveness Rate</h4>
                <p className="text-xs text-gray-600 mb-1">% of controls operating effectively</p>
                <p className="text-xs italic">Example: "87% of NIST controls fully effective"</p>
              </div>
              
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Audit Finding Closure Rate</h4>
                <p className="text-xs text-gray-600 mb-1">Remediation progress</p>
                <p className="text-xs italic">Example: "18 of 22 audit findings closed on time"</p>
              </div>
              
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Policy Compliance Rate</h4>
                <p className="text-xs text-gray-600 mb-1">Adherence to security policies</p>
                <p className="text-xs italic">Example: "94% compliance across business units"</p>
              </div>
              
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Training Completion Rate</h4>
                <p className="text-xs text-gray-600 mb-1">Security awareness progress</p>
                <p className="text-xs italic">Example: "98% completed annual training"</p>
              </div>
              
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-sm mb-1">Regulatory Readiness Score</h4>
                <p className="text-xs text-gray-600 mb-1">Preparedness for compliance</p>
                <p className="text-xs italic">Example: "SEC 4-day disclosure process tested & ready"</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h3 className="font-bold mb-3 text-lg">🎯 Metric Selection Best Practices</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-semibold mb-2">DO:</h4>
            <ul className="space-y-1 text-gray-700">
              <li>✓ Use 5-7 key metrics per board report (not 50)</li>
              <li>✓ Focus on outcomes, not activities</li>
              <li>✓ Show trends over time (QoQ, YoY)</li>
              <li>✓ Translate metrics into dollar impact</li>
              <li>✓ Benchmark against industry peers</li>
              <li>✓ Use visualizations (charts, heat maps)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">DON'T:</h4>
            <ul className="space-y-1 text-gray-700">
              <li>✗ Report raw alert volumes or ticket counts</li>
              <li>✗ Use technical metrics boards don't understand</li>
              <li>✗ Present metrics without context or narrative</li>
              <li>✗ Cherry-pick only positive metrics</li>
              <li>✗ Change metrics frequently (maintain consistency)</li>
              <li>✗ Ignore metrics that are getting worse</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Organizational Structure Tab
  const StructureTab = () => (
    <div className="space-y-6">
      <div className="bg-white/90 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">CISO Organizational Structure</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Where does your CISO report?</label>
          <select 
            value={organizationalLevel}
            onChange={(e) => setOrganizationalLevel(e.target.value)}
            className="w-full md:w-96 p-2 border rounded-lg"
          >
            <option value="">Select reporting structure...</option>
            <option value="ceo">CEO (Direct Report)</option>
            <option value="board">Board of Directors</option>
            <option value="cro">Chief Risk Officer (CRO)</option>
            <option value="cio">Chief Information Officer (CIO)</option>
            <option value="cfo">Chief Financial Officer (CFO)</option>
            <option value="coo">Chief Operating Officer (COO)</option>
            <option value="cto">Chief Technology Officer (CTO)</option>
            <option value="lower">3+ levels from CEO</option>
          </select>
        </div>

        {organizationalLevel && (
          <div className={`p-6 rounded-lg mb-6 ${
            ['ceo', 'board', 'cro'].includes(organizationalLevel) 
              ? 'bg-green-50 border border-green-200' 
              : ['cio', 'cto'].includes(organizationalLevel)
              ? 'bg-yellow-50 border border-yellow-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <h3 className="font-bold mb-3">
              {['ceo', 'board', 'cro'].includes(organizationalLevel) && '✓ Strong Positioning'}
              {['cio', 'cto', 'cfo', 'coo'].includes(organizationalLevel) && '⚠️ Common But Challenging'}
              {organizationalLevel === 'lower' && '❌ High Risk Structure'}
            </h3>
            
            {organizationalLevel === 'ceo' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-green-800">Best practice for most organizations</p>
                <p><strong>Pros:</strong></p>
                <ul className="ml-4">
                  <li>• Direct access to top leadership and strategic decisions</li>
                  <li>• Enhanced organizational authority and budget influence</li>
                  <li>• Clear accountability without conflicts of interest</li>
                  <li>• Security elevated to business-critical function</li>
                </ul>
                <p><strong>Considerations:</strong></p>
                <ul className="ml-4">
                  <li>• Requires strong business acumen and communication skills</li>
                  <li>• Must speak business language, not just technical</li>
                  <li>• Need dotted lines to CIO, Legal, and Risk teams</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'board' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-green-800">Emerging model for high-maturity organizations</p>
                <p><strong>Pros:</strong></p>
                <ul className="ml-4">
                  <li>• Maximum visibility and governance oversight</li>
                  <li>• Strong signal to market about security commitment</li>
                  <li>• Direct board communication without filters</li>
                  <li>• Common in highly regulated industries</li>
                </ul>
                <p><strong>Considerations:</strong></p>
                <ul className="ml-4">
                  <li>• Still need operational reporting line to CEO/COO</li>
                  <li>• Can create complex dual-reporting dynamics</li>
                  <li>• Best for mature security programs</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'cro' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-green-800">Ideal for enterprise risk management alignment</p>
                <p><strong>Pros:</strong></p>
                <ul className="ml-4">
                  <li>• Aligns cyber risk with enterprise risk framework</li>
                  <li>• Strong for financial services and regulated industries</li>
                  <li>• Facilitates risk quantification and prioritization</li>
                  <li>• CRO typically reports directly to CEO/Board</li>
                </ul>
                <p><strong>Considerations:</strong></p>
                <ul className="ml-4">
                  <li>• Requires mature ERM function</li>
                  <li>• May need technical collaboration with CIO</li>
                  <li>• Works best when CRO has cyber understanding</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'cio' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-yellow-700">Most common structure but creates conflicts</p>
                <p><strong>Challenges:</strong></p>
                <ul className="ml-4">
                  <li>• Competing priorities: velocity vs security</li>
                  <li>• CIO can veto security decisions</li>
                  <li>• Security seen as IT function, not business function</li>
                  <li>• Difficult to report security issues about IT</li>
                </ul>
                <p><strong>When it works:</strong></p>
                <ul className="ml-4">
                  <li>• CIO has strong security background</li>
                  <li>• CISO has dotted line to CEO or Board</li>
                  <li>• Organization has mature security program</li>
                  <li>• Clear escalation paths exist for conflicts</li>
                </ul>
                <p className="mt-2 p-2 bg-yellow-100 rounded">
                  <strong>Recommendation:</strong> If reporting to CIO, negotiate dotted line to CEO and quarterly board access
                </p>
              </div>
            )}
            
            {organizationalLevel === 'cfo' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-yellow-700">Risk: Security seen primarily as cost center</p>
                <p><strong>Challenges:</strong></p>
                <ul className="ml-4">
                  <li>• Security viewed through financial lens only</li>
                  <li>• Budget constraints may override security needs</li>
                  <li>• Limited understanding of technical risks</li>
                  <li>• Often indicates compliance-checkbox mentality</li>
                </ul>
                <p><strong>When it works:</strong></p>
                <ul className="ml-4">
                  <li>• Small organizations without dedicated CIO</li>
                  <li>• CFO has strong GRC background</li>
                  <li>• Security primarily focused on compliance</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'coo' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-yellow-700">Viable if COO has technical background</p>
                <p><strong>Pros:</strong></p>
                <ul className="ml-4">
                  <li>• Aligns security with business operations</li>
                  <li>• COO typically close to CEO</li>
                  <li>• Can integrate security into processes</li>
                </ul>
                <p><strong>Considerations:</strong></p>
                <ul className="ml-4">
                  <li>• COO must understand technology and risk</li>
                  <li>• May prioritize operational efficiency over security</li>
                  <li>• Less common structure</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'cto' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-yellow-700">Similar challenges to CIO reporting</p>
                <p><strong>Challenges:</strong></p>
                <ul className="ml-4">
                  <li>• CTO focused on innovation and speed</li>
                  <li>• Security may slow product development</li>
                  <li>• Potential conflict of interest</li>
                </ul>
                <p><strong>When it works:</strong></p>
                <ul className="ml-4">
                  <li>• Technology companies with security-first culture</li>
                  <li>• CTO deeply values security</li>
                  <li>• CISO has strong product security expertise</li>
                </ul>
              </div>
            )}
            
            {organizationalLevel === 'lower' && (
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-red-700">High risk - CISO lacks organizational authority</p>
                <p><strong>Critical Issues:</strong></p>
                <ul className="ml-4">
                  <li>• Multiple layers between CISO and decision-makers</li>
                  <li>• Security decisions can be blocked/delayed</li>
                  <li>• No board visibility or strategic influence</li>
                  <li>• Indicates low organizational security maturity</li>
                  <li>• High probability of security incidents</li>
                </ul>
                <p className="mt-2 p-2 bg-red-100 rounded">
                  <strong>Urgent Action Required:</strong> Advocate for organizational restructuring. Present business case showing correlation between CISO positioning and security outcomes.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <TrendingUp className="text-green-600" size={20} />
              Ideal Reporting Structure
            </h3>
            <div className="space-y-3 text-sm text-gray-700">
              <p><strong>Recommended Hierarchy:</strong></p>
              <ol className="ml-4 space-y-2">
                <li>1. <strong>CEO Direct Report</strong> - Best for most organizations</li>
                <li>2. <strong>Chief Risk Officer</strong> - Strong ERM alignment</li>
                <li>3. <strong>Board Committee</strong> - Maximum oversight</li>
              </ol>
              <p className="mt-3"><strong>Essential Elements:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• Direct quarterly board meeting access</li>
                <li>• Monthly risk committee participation</li>
                <li>• Independent budget authority</li>
                <li>• Cross-functional collaboration authority</li>
                <li>• Clear escalation path to CEO</li>
              </ul>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <GitBranch className="text-blue-600" size={20} />
              Alternative: "Office of the CISO"
            </h3>
            <div className="space-y-3 text-sm text-gray-700">
              <p>Emerging model for large organizations:</p>
              <ul className="ml-4 space-y-2">
                <li>• <strong>Chief CISO</strong> - Overall security strategy, reports to CEO</li>
                <li>• <strong>Business Unit CISOs</strong> - Embedded in divisions</li>
                <li>• <strong>Functional CISOs</strong> - Product, Cloud, OT security</li>
                <li>• <strong>Regional CISOs</strong> - Geographic coverage</li>
              </ul>
              <p className="mt-3"><strong>Benefits:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• Distributed responsibility</li>
                <li>• Specialized expertise</li>
                <li>• Better business alignment</li>
                <li>• Reduced single point of failure</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h3 className="font-bold mb-3">📊 2025 CISO Reporting Line Statistics (IANS Research)</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">47%</div>
            <div className="text-gray-700">Report to CIO</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">27%</div>
            <div className="text-gray-700">Report to CEO</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">8%</div>
            <div className="text-gray-700">Report to CTO</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">8%</div>
            <div className="text-gray-700">Report to CRO</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">10%</div>
            <div className="text-gray-700">CFO / COO / Other</div>
          </div>
        </div>
        <p className="text-sm text-gray-700 mt-4">
          CEO-direct CISO reporting has grown significantly — from roughly 3% in 2020 to 27% in 2025 —
          reflecting boards' increased recognition of cybersecurity as a strategic business risk.
          The 47% still reporting to the CIO face the greatest structural challenges around independence,
          board access, and security-vs-velocity conflicts.
        </p>
      </div>
    </div>
  );

  // SEC Compliance Tab
  const ComplianceTab = () => (
    <div className="space-y-6">
      <div className="bg-white/90 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">SEC Cybersecurity Disclosure Requirements</h2>
        
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            Critical Compliance Deadlines
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p><strong>Material Incidents:</strong> Disclosure required within 4 business days via Form 8-K</p>
            <p><strong>Annual Disclosures:</strong> Risk management, strategy, and governance on Form 10-K</p>
            <p><strong>Effective Date:</strong> Rules effective December 18, 2023. As of 2026, the SEC is in active enforcement mode — non-compliance is no longer a theoretical risk.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 p-4">
              <h3 className="font-bold flex items-center gap-2">
                <Calendar className="text-blue-600" size={20} />
                Form 8-K: Material Incident Disclosure (4-Day Rule)
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h4 className="font-semibold mb-2">What Must Be Disclosed:</h4>
                <ul className="text-sm space-y-1 text-gray-700 ml-4">
                  <li>• Nature, scope, and timing of the incident</li>
                  <li>• Material impact or reasonably likely material impact</li>
                  <li>• Effect on financial condition and operations</li>
                  <li>• Any information not yet determined must be disclosed when known</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Materiality Determination:</h4>
                <ul className="text-sm space-y-1 text-gray-700 ml-4">
                  <li>• Qualitative factors: reputational damage, competitive harm</li>
                  <li>• Quantitative factors: financial impact, operational disruption</li>
                  <li>• Consider cumulative effect of multiple related incidents</li>
                  <li>• Document materiality assessment process</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">National Security Exception:</h4>
                <p className="text-sm text-gray-700">
                  Disclosure may be delayed if U.S. Attorney General determines immediate disclosure 
                  would pose substantial risk to national security or public safety
                </p>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <p className="text-sm font-semibold mb-1">⚠️ Important:</p>
                <p className="text-sm text-gray-700">
                  Clock starts when materiality is determined, not when incident is discovered. 
                  Organizations need processes to make rapid materiality determinations.
                </p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 p-4">
              <h3 className="font-bold flex items-center gap-2">
                <FileText className="text-green-600" size={20} />
                Form 10-K: Annual Risk Management Disclosures
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h4 className="font-semibold mb-2">Required Disclosures:</h4>
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded">
                    <p className="font-semibold text-sm mb-1">1. Risk Management Processes</p>
                    <ul className="text-sm text-gray-700 ml-4">
                      <li>• How you assess, identify, and manage material cyber risks</li>
                      <li>• Whether cyber risk is part of overall business strategy</li>
                      <li>• Impact of cyber risks on business operations</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded">
                    <p className="font-semibold text-sm mb-1">2. Management's Role</p>
                    <ul className="text-sm text-gray-700 ml-4">
                      <li>• Positions/committees responsible for cyber risk</li>
                      <li>• Relevant expertise of these individuals</li>
                      <li>• Processes for informing management about risks</li>
                      <li>• How often management reports to the board</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded">
                    <p className="font-semibold text-sm mb-1">3. Board Oversight</p>
                    <ul className="text-sm text-gray-700 ml-4">
                      <li>• Board committee responsible for cyber oversight</li>
                      <li>• Processes by which board is informed about risks</li>
                      <li>• Frequency of cyber risk discussions</li>
                      <li>• How board oversees cyber risk management</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-semibold mb-1">📝 Note:</p>
                <p className="text-sm text-gray-700">
                  SEC removed proposed requirement to disclose board members' cybersecurity expertise. 
                  Focus on processes, not specific policies that could give attackers a roadmap.
                </p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 p-4">
              <h3 className="font-bold flex items-center gap-2">
                <CheckCircle className="text-purple-600" size={20} />
                Compliance Readiness Checklist
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Incident response plan updated</strong> to include materiality assessment process
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Materiality determination committee</strong> established (Legal, Finance, Security, Communications)
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>4-day disclosure process</strong> documented and tested via tabletop exercises
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Board oversight processes</strong> documented for 10-K disclosure
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Management roles and expertise</strong> clearly defined and documented
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Risk management processes</strong> mapped to disclosure requirements
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Disclosure controls</strong> integrated with incident response procedures
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Cross-functional coordination</strong> between Security, Legal, Finance, and IR teams
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Board reporting cadence</strong> established (quarterly minimum)
                  </span>
                </label>
                
                <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" className="mt-1" />
                  <span className="text-sm text-gray-700">
                    <strong>Cyber insurance coverage</strong> reviewed for adequacy and disclosure requirements
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-red-50 p-6 rounded-lg border border-red-200">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <Scale className="text-red-600" size={20} />
          Enforcement & Penalties
        </h3>
        <div className="space-y-2 text-sm text-gray-700">
          <p><strong>SEC Enforcement Powers:</strong></p>
          <ul className="ml-4 space-y-1">
            <li>• Civil penalties for non-compliance</li>
            <li>• Enforcement actions against companies and individuals</li>
            <li>• Loss of investor confidence and market value</li>
            <li>• Personal liability for executives and board members</li>
          </ul>
          <p className="mt-3"><strong>Notable Enforcement Actions:</strong></p>
          <ul className="ml-4 space-y-1">
            <li>• <strong>SolarWinds (2023–2024):</strong> SEC charged SolarWinds and its CISO with fraud and internal control failures related to cybersecurity disclosures — a landmark case establishing personal CISO liability</li>
            <li>• <strong>Progress Software (2024):</strong> SEC investigation into MOVEit breach disclosures underscored 8-K materiality determination obligations</li>
            <li>• <strong>2025 trend:</strong> The SEC Division of Enforcement has signaled continued focus on disclosure accuracy, materiality assessment documentation, and board oversight evidence in 10-K filings</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Global Frameworks Tab
  const GlobalFrameworksTab = () => {
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [selectedFramework, setSelectedFramework] = useState('');
    
    return (
      <div className="space-y-6">
        <div className="bg-white/90 p-6 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Global Cybersecurity Frameworks & Standards</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Filter by Region:</label>
            <select 
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full md:w-64 p-2 border rounded-lg"
            >
              <option value="all">All Regions</option>
              <option value="eu">European Union</option>
              <option value="us">United States</option>
              <option value="apac">Asia-Pacific</option>
              <option value="global">Global/International</option>
            </select>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6 border border-blue-200">
            <h3 className="font-bold text-lg mb-3">Why Global Frameworks Matter for Boards</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Compliance Obligations:</h4>
                <ul className="space-y-1 text-gray-700">
                  <li>• Multi-jurisdictional operations require framework compliance</li>
                  <li>• Customers demand certifications (ISO 27001, SOC 2)</li>
                  <li>• Regulatory penalties for non-compliance can be severe</li>
                  <li>• M&A due diligence examines compliance posture</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Strategic Value:</h4>
                <ul className="space-y-1 text-gray-700">
                  <li>• Frameworks provide structured governance models</li>
                  <li>• Competitive advantage in regulated industries</li>
                  <li>• Risk reduction through proven controls</li>
                  <li>• 60-80% overlap enables efficient multi-compliance</li>
                </ul>
              </div>
            </div>
          </div>

          {/* European Union Frameworks */}
          {(selectedRegion === 'all' || selectedRegion === 'eu') && (
            <div className="border-2 border-blue-600 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-2xl">🇪🇺</span>
                European Union Regulations
              </h3>

              <div className="space-y-4">
                {/* GDPR */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">GDPR (General Data Protection Regulation)</h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active since 2018</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Any organization processing EU citizen personal data</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Approve data protection policies and DPO appointment</li>
                      <li>• Ensure lawful basis for data processing</li>
                      <li>• Oversee data breach notification procedures (72-hour rule)</li>
                      <li>• Review Data Protection Impact Assessments (DPIAs)</li>
                      <li>• Understand penalties: up to €20M or 4% of global revenue</li>
                    </ul>
                    <p className="mt-2"><strong>Key Metrics for Board:</strong> Number of DPIAs completed, data subject requests processed, breach notification compliance rate, cross-border data transfer mechanisms</p>
                  </div>
                </div>

                {/* NIS2 Directive */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">NIS2 (Network and Information Systems Directive 2)</h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active: Transposed Oct 2024</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Essential (energy, transport, health, banking) and important (postal, waste, chemicals, food) entities operating in the EU</p>
                    <p><strong>Status (2026):</strong> Transposition deadline was October 17, 2024. Most EU member states have enacted national legislation; enforcement is actively underway. Non-EU companies serving EU customers in covered sectors must also comply.</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Approve cybersecurity risk management measures</li>
                      <li>• Oversee incident reporting (24 hours early warning, 72 hours detailed)</li>
                      <li>• Ensure supply chain security policies</li>
                      <li>• Personal liability: Board members can be held personally accountable</li>
                      <li>• Mandatory cybersecurity training for management and board</li>
                      <li>• Penalties: up to €10M or 2% of global revenue (essential), €7M or 1.4% (important)</li>
                    </ul>
                    <p className="mt-2"><strong>Key Differences from NIS1:</strong> Broader scope, mandatory board training, supply chain focus, management accountability, harmonized enforcement</p>
                    <p className="mt-2"><strong>Overlap:</strong> 80% alignment with ISO 27001 controls</p>
                  </div>
                </div>

                {/* DORA */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">DORA (Digital Operational Resilience Act)</h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Mandatory: Jan 17, 2025</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Financial entities (banks, insurers, payment processors, crypto providers) and critical ICT third-party providers</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Approve ICT risk management framework</li>
                      <li>• Oversee digital operational resilience testing (including TLPT)</li>
                      <li>• Review third-party ICT service provider oversight</li>
                      <li>• Ensure incident classification and reporting capabilities</li>
                      <li>• Participate in information sharing arrangements</li>
                      <li>• Board training mandatory on ICT risk and operational resilience</li>
                    </ul>
                    <p className="mt-2"><strong>Testing Requirements:</strong> Annual penetration testing, Threat-Led Penetration Testing (TLPT) every 3 years for significant entities</p>
                    <p className="mt-2"><strong>Critical Third-Party Oversight:</strong> ESAs (EBA, EIOPA, ESMA) have been designating and overseeing Critical ICT Third-Party Providers (CTPPs) since July 2025. First oversight cycles are underway in 2026.</p>
                    <p className="mt-2"><strong>Penalties:</strong> Member state determined, significant financial penalties and operational restrictions</p>
                    <div className="bg-white p-3 rounded mt-2">
                      <p className="font-semibold text-xs mb-1">⚠️ DORA vs NIS2:</p>
                      <p className="text-xs">Financial entities follow DORA (lex specialis), exempt from NIS2 cyber provisions. Non-financial critical infrastructure follows NIS2.</p>
                    </div>
                  </div>
                </div>

                {/* CER Directive */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">CER (Critical Entities Resilience Directive)</h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Transposed: Oct 2024</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Critical entities in 11 sectors (energy, transport, banking, health, water, etc.)</p>
                    <p><strong>Focus:</strong> Physical and cyber resilience of critical infrastructure</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Oversee resilience measures for both physical and cyber threats</li>
                      <li>• Ensure business continuity and crisis management plans</li>
                      <li>• Address natural hazards, terrorism, sabotage, and insider threats</li>
                      <li>• Coordinate with NIS2 for cyber-specific requirements</li>
                    </ul>
                    <p className="mt-2"><strong>Relationship:</strong> Complements NIS2 by adding physical resilience dimension</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* United States Frameworks */}
          {(selectedRegion === 'all' || selectedRegion === 'us') && (
            <div className="border-2 border-red-600 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-2xl">🇺🇸</span>
                United States Standards
              </h3>

              <div className="space-y-4">
                {/* NIST CSF */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">NIST Cybersecurity Framework (CSF 2.0)</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Voluntary</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> All organizations, mandatory for federal agencies, widely adopted by critical infrastructure and private sector</p>
                    <p><strong>Core Functions (CSF 2.0, 2024):</strong> Govern, Identify, Protect, Detect, Respond, Recover — 6 functions. The new Govern function places board-level strategy, policy, and oversight at the center of the framework, making board engagement a first-class requirement.</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Approve cybersecurity strategy aligned with business objectives</li>
                      <li>• Oversee implementation across six core functions</li>
                      <li>• Review maturity assessments and gap analyses</li>
                      <li>• Ensure adequate resources for framework implementation</li>
                      <li>• Monitor progress through Implementation Tiers (Partial → Adaptive)</li>
                    </ul>
                    <p className="mt-2"><strong>Board Advantage:</strong> Risk-based, flexible, aligns with business objectives, maps to other frameworks</p>
                  </div>
                </div>

                {/* SOC 2 */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">SOC 2 (System and Organization Controls)</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Voluntary / Customer-Driven</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Service organizations handling customer data (SaaS, cloud providers, data centers)</p>
                    <p><strong>Trust Service Principles:</strong> Security (mandatory), Availability, Processing Integrity, Confidentiality, Privacy</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Decide between Type I (point-in-time) vs Type II (3-12 months)</li>
                      <li>• Approve scope and applicable Trust Service Criteria</li>
                      <li>• Review auditor selection and engagement terms</li>
                      <li>• Understand that SOC 2 is a report, not a certification</li>
                      <li>• Consider competitive advantage in B2B sales</li>
                    </ul>
                    <p className="mt-2"><strong>Customer Impact:</strong> Often required in vendor contracts, enables enterprise sales, builds customer trust</p>
                    <p className="mt-2"><strong>Overlap:</strong> 80% control overlap with ISO 27001, 60% with PCI DSS</p>
                  </div>
                </div>

                {/* PCI DSS */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">PCI DSS (Payment Card Industry Data Security Standard)</h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Mandatory for card data</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Any entity that stores, processes, or transmits cardholder data</p>
                    <p><strong>Version:</strong> PCI DSS v4.0.1 (published June 2024). v3.2.1 was retired March 31, 2025. All previously "future-dated" v4.0 requirements became mandatory March 31, 2025 — organizations should now be fully compliant with the complete v4.0.1 control set.</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Understand merchant level (1-4) and assessment requirements</li>
                      <li>• Approve annual compliance validation approach (SAQ or ROC)</li>
                      <li>• Review quarterly network scans by Approved Scanning Vendor</li>
                      <li>• Oversee cardholder data environment (CDE) segmentation</li>
                      <li>• Ensure penalties are understood: $5-100K per month, card brand fines</li>
                    </ul>
                    <p className="mt-2"><strong>12 Requirements:</strong> Firewalls, encryption, access control, monitoring, testing, security policy</p>
                    <p className="mt-2"><strong>Business Impact:</strong> Non-compliance can result in loss of card processing ability</p>
                  </div>
                </div>

                {/* HIPAA */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">HIPAA (Health Insurance Portability and Accountability Act)</h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Mandatory for healthcare</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Healthcare providers, health plans, clearinghouses, business associates handling PHI</p>
                    <p><strong>Key Rules:</strong> Privacy Rule, Security Rule, Breach Notification Rule</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Oversee HIPAA Security Risk Assessment (required annually)</li>
                      <li>• Approve Business Associate Agreements (BAAs) with vendors</li>
                      <li>• Review breach notification procedures (60-day HHS reporting)</li>
                      <li>• Ensure encryption and access controls for ePHI</li>
                      <li>• Understand penalties: $100-$50K per violation, up to $1.5M annually</li>
                      <li>• Monitor OCR enforcement trends and audit activity</li>
                    </ul>
                    <p className="mt-2"><strong>ISO 27001 Benefit:</strong> ISO 27001 provides structured framework for HIPAA security compliance</p>
                  </div>
                </div>

                {/* SEC Cyber Rules */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">SEC Cybersecurity Disclosure Rules</h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Mandatory: Dec 2023</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> All SEC-registered public companies</p>
                    <p><strong>Requirements:</strong> Form 8-K (4 days), Form 10-K annual disclosures</p>
                    <p className="text-xs italic">See "SEC Compliance" tab for complete details</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* International/Global Frameworks */}
          {(selectedRegion === 'all' || selectedRegion === 'global') && (
            <div className="border-2 border-green-600 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-2xl">🌍</span>
                International Standards
              </h3>

              <div className="space-y-4">
                {/* ISO 27001 */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">ISO/IEC 27001:2022 - Information Security Management</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Voluntary Certification</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Global standard for Information Security Management System (ISMS)</p>
                    <p><strong>Latest Update:</strong> ISO 27001:2022 — transition from the 2013 version was mandatory by October 31, 2025. Organizations still on the 2013 standard as of 2026 are no longer certified. If your certificate hasn't been updated, this is an urgent gap.</p>
                    <p><strong>Board Responsibilities:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Approve ISMS scope, policy, and objectives</li>
                      <li>• Allocate resources for ISMS implementation and certification</li>
                      <li>• Review risk assessments and Statement of Applicability (SoA)</li>
                      <li>• Participate in management review meetings</li>
                      <li>• Understand 93 Annex A controls (updated from 114 in 2013 version)</li>
                      <li>• Support external audit process (Stage 1 and Stage 2)</li>
                    </ul>
                    <p className="mt-2"><strong>Certification Value:</strong> Global recognition, customer requirement, competitive advantage, framework foundation</p>
                    <p className="mt-2"><strong>Overlap:</strong> 80% with NIS2, 80% with SOC 2, strong alignment with NIST CSF</p>
                    <p className="mt-2"><strong>Key Changes in 2022:</strong> New controls for threat intelligence, cloud security, configuration management</p>
                  </div>
                </div>

                {/* ISO 27002 */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">ISO/IEC 27002:2022 - Code of Practice</h4>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Reference Guide</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Purpose:</strong> Provides implementation guidance for ISO 27001 Annex A controls</p>
                    <p><strong>Structure:</strong> 93 controls across 4 themes (Organizational, People, Physical, Technological)</p>
                    <p><strong>Board Use:</strong> Reference for understanding control implementation details, not a certifiable standard</p>
                  </div>
                </div>

                {/* CIS Controls */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">CIS Controls v8.1</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Free Framework</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> Prioritized set of actions to protect against the most common attacks</p>
                    <p><strong>18 Controls:</strong> Grouped by Implementation Groups (IG1, IG2, IG3). v8.1 (2024) refined safe software development, updated cloud guidance, and sharpened asset management controls.</p>
                    <p><strong>Board Value:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Tactical, actionable controls with clear implementation steps</li>
                      <li>• Cost-effective for organizations with limited budgets</li>
                      <li>• IG1 (basic) can be achieved without massive investment</li>
                      <li>• Aligns with NIST CSF and can support ISO 27001</li>
                    </ul>
                    <p className="mt-2"><strong>Top 5 Controls:</strong> Asset inventory, software inventory, data protection, secure configuration, account management</p>
                  </div>
                </div>

                {/* ISO 22301 */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">ISO 22301 - Business Continuity Management</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Voluntary Certification</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>Purpose:</strong> Business Continuity Management System (BCMS)</p>
                    <p><strong>Relationship to Cyber:</strong> Complements ISO 27001, addresses operational resilience including cyber incidents</p>
                    <p><strong>Board Value:</strong> Demonstrates commitment to continuity, increasingly important for supply chain resilience</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* APAC Frameworks */}
          {(selectedRegion === 'all' || selectedRegion === 'apac') && (
            <div className="border-2 border-purple-600 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-2xl">🌏</span>
                Asia-Pacific Requirements
              </h3>

              <div className="space-y-4">
                {/* Singapore */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">🇸🇬 Singapore - MTCS & Cybersecurity Act</h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Active</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>MTCS (Multi-Tier Cloud Security):</strong> Singapore Standard SS 584:2020</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Cloud service provider certification scheme</li>
                      <li>• Three security levels: Level 1 (basic), Level 2 (business), Level 3 (high sensitivity)</li>
                      <li>• Recognized through MRAs with Finland, Germany, South Korea</li>
                    </ul>
                    <p className="mt-2"><strong>Cybersecurity Act 2018 (Amended 2024):</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• Critical Information Infrastructure (CII) protection</li>
                      <li>• Mandatory incident reporting for CII owners</li>
                      <li>• Cybersecurity Service Providers licensing</li>
                      <li>• Powers for Cyber Security Agency (CSA) oversight</li>
                    </ul>
                    <p><strong>PDPA (Personal Data Protection Act):</strong> Singapore's data privacy law, aligns with GDPR principles</p>
                  </div>
                </div>

                {/* Australia */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">🇦🇺 Australia - ISM & SOCI Act</h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Active</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>ISM (Information Security Manual):</strong> Updated quarterly by the Australian Signals Directorate (ASD). Check asd.gov.au for the current version.</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Produced by Australian Signals Directorate (ASD)</li>
                      <li>• Risk-based framework for IT/OT systems protection</li>
                      <li>• Five functions: Govern, Identify, Protect, Detect, Respond</li>
                      <li>• <strong>Board Requirement (ISM-2000):</strong> "The board of directors or executive committee seeks regular briefings on cybersecurity posture"</li>
                      <li>• <strong>Culture (ISM-2001):</strong> Board champions positive cybersecurity culture</li>
                    </ul>
                    <p className="mt-2"><strong>Essential Eight:</strong> Baseline mitigation strategies (application control, patching, MFA, backups)</p>
                    <p className="mt-2"><strong>SOCI Act:</strong> Security of Critical Infrastructure Act 2018</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Applies to critical infrastructure sectors (energy, transport, telecom, water, health)</li>
                      <li>• Mandatory cyber incident reporting</li>
                      <li>• Risk management programs required</li>
                    </ul>
                  </div>
                </div>

                {/* Japan */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">🇯🇵 Japan - PIPA & Cybersecurity Standards</h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Active</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>PIPA (Personal Information Protection Act):</strong> Amended 2022</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Japan's comprehensive data privacy law</li>
                      <li>• Mandatory breach notification to PPC (Personal Information Protection Commission)</li>
                      <li>• Cross-border transfer restrictions</li>
                    </ul>
                    <p className="mt-2"><strong>METI IoT Security Labeling:</strong> Voluntary scheme active since March 2025</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Baseline and category-specific security for IoT products</li>
                      <li>• Self-declaration or third-party evaluation</li>
                      <li>• Aligned with international standards (ETSI EN 303 645, NIST IR 8259)</li>
                    </ul>
                    <p className="mt-2"><strong>Critical Infrastructure:</strong> Over $3.8B allocated to ICS and critical infrastructure protection in 2025</p>
                  </div>
                </div>

                {/* India */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">🇮🇳 India - DPDP Act & IT Rules</h4>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Evolving</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>DPDP Act 2023:</strong> Digital Personal Data Protection Act</p>
                    <ul className="ml-4 space-y-1">
                      <li>• India's comprehensive data protection law</li>
                      <li>• Applies to processing of digital personal data within India and cross-border</li>
                      <li>• Individual consent and data principal rights (access, correction, erasure)</li>
                      <li>• Implementing rules and enforcement mechanisms were advancing through 2025–2026; verify current status with legal counsel for India operations</li>
                    </ul>
                    <p className="mt-2"><strong>IT Act 2000 & Rules:</strong> Information Technology Act and cybersecurity provisions</p>
                  </div>
                </div>

                {/* China */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg">🇨🇳 China - PIPL, CSL & MLPS</h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Active</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>PIPL (Personal Information Protection Law):</strong> Effective November 2021</p>
                    <p><strong>CSL (Cybersecurity Law):</strong> Network security and data localization</p>
                    <p><strong>MLPS (Multi-Level Protection Scheme):</strong> Graded cybersecurity protection (Levels 1-5)</p>
                    <p className="mt-2"><strong>Board Consideration:</strong> Complex regulatory environment with data localization requirements</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Framework Overlap Matrix */}
        <div className="bg-white/90 p-6 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4">Framework Overlap & Integration Strategy</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">If You Have...</th>
                  <th className="border p-2 text-left">Overlap with...</th>
                  <th className="border p-2 text-left">% Overlap</th>
                  <th className="border p-2 text-left">Integration Strategy</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2 font-semibold">ISO 27001</td>
                  <td className="border p-2">SOC 2, NIS2, NIST CSF, HIPAA</td>
                  <td className="border p-2">80%, 80%, 70%, 65%</td>
                  <td className="border p-2">Use ISO 27001 as governance backbone, map controls to other frameworks</td>
                </tr>
                <tr>
                  <td className="border p-2 font-semibold">SOC 2</td>
                  <td className="border p-2">ISO 27001, PCI DSS</td>
                  <td className="border p-2">80%, 60%</td>
                  <td className="border p-2">Leverage shared controls, centralize evidence collection</td>
                </tr>
                <tr>
                  <td className="border p-2 font-semibold">NIST CSF</td>
                  <td className="border p-2">ISO 27001, NIS2, CIS Controls</td>
                  <td className="border p-2">70%, 65%, 75%</td>
                  <td className="border p-2">Use as strategic framework, map to tactical standards</td>
                </tr>
                <tr>
                  <td className="border p-2 font-semibold">DORA</td>
                  <td className="border p-2">NIS2, ISO 27001, GDPR</td>
                  <td className="border p-2">Significant, Strong, Moderate</td>
                  <td className="border p-2">DORA takes precedence for financial entities (lex specialis)</td>
                </tr>
                <tr>
                  <td className="border p-2 font-semibold">PCI DSS</td>
                  <td className="border p-2">SOC 2, ISO 27001</td>
                  <td className="border p-2">60%, 50%</td>
                  <td className="border p-2">Scope PCI to CDE, use ISO/SOC for broader security</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold mb-2">✓ Efficiency Strategy</h4>
              <ul className="text-sm space-y-1">
                <li>• Start with ISO 27001 as foundation</li>
                <li>• Use control mapping to show compliance</li>
                <li>• Centralize evidence repository</li>
                <li>• Coordinate audit schedules</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-2">📊 Board Communication</h4>
              <ul className="text-sm space-y-1">
                <li>• Present unified compliance posture</li>
                <li>• Show coverage across frameworks</li>
                <li>• Highlight gaps requiring investment</li>
                <li>• Report on audit calendar and costs</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold mb-2">⚠️ Common Pitfalls</h4>
              <ul className="text-sm space-y-1">
                <li>• Treating each framework in isolation</li>
                <li>• Not leveraging control overlap</li>
                <li>• Duplicate evidence collection efforts</li>
                <li>• Missing harmonization opportunities</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Board Questions */}
        <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
          <h3 className="font-bold mb-3 text-lg">❓ Questions Boards Should Ask About Global Frameworks</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold mb-2">Compliance Strategy:</p>
              <ul className="space-y-1 text-gray-700">
                <li>1. Which frameworks apply to our business and why?</li>
                <li>2. What is our multi-framework compliance strategy?</li>
                <li>3. Are we leveraging control overlap effectively?</li>
                <li>4. What are the financial penalties for non-compliance?</li>
                <li>5. Do we have the right certifications for our market?</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-2">Implementation & ROI:</p>
              <ul className="space-y-1 text-gray-700">
                <li>6. What is the cost vs benefit of each certification?</li>
                <li>7. Are we audit-ready for all applicable frameworks?</li>
                <li>8. How do customers/partners value these certifications?</li>
                <li>9. What competitive advantage do certifications provide?</li>
                <li>10. Are we prepared for evolving regulatory landscape?</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Board Engagement Tab
  const EngagementTab = () => (
    <div className="space-y-6">
      <div className="bg-white/90 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Board Engagement & Education</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Board Cybersecurity Maturity:</label>
          <select 
            value={boardMaturity}
            onChange={(e) => setBoardMaturity(e.target.value)}
            className="w-full md:w-96 p-2 border rounded-lg"
          >
            <option value="">Assess your board's cyber maturity...</option>
            <option value="unaware">Unaware - Limited cyber understanding</option>
            <option value="aware">Aware - Basic understanding, reactive</option>
            <option value="engaged">Engaged - Regular discussions, proactive</option>
            <option value="champion">Champion - Strategic partner, cyber-savvy</option>
          </select>
        </div>

        {boardMaturity && (
          <div className={`p-6 rounded-lg mb-6 ${
            boardMaturity === 'champion' ? 'bg-green-50 border border-green-200' : 
            boardMaturity === 'engaged' ? 'bg-blue-50 border border-blue-200' :
            boardMaturity === 'aware' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <h3 className="font-bold mb-3">
              {boardMaturity === 'champion' && '🏆 Cyber-Champion Board'}
              {boardMaturity === 'engaged' && '✓ Engaged Board'}
              {boardMaturity === 'aware' && '⚠️ Awareness-Stage Board'}
              {boardMaturity === 'unaware' && '❌ Unaware Board'}
            </h3>
            
            {boardMaturity === 'unaware' && (
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-red-700">Critical action required to elevate board cyber literacy</p>
                <p><strong>Recommended Immediate Actions:</strong></p>
                <ol className="ml-4 space-y-2">
                  <li>1. <strong>Schedule cyber 101 education session</strong> - Basic threat landscape and business impact</li>
                  <li>2. <strong>Share industry breach case studies</strong> - Board-level consequences (CEO firings, lawsuits)</li>
                  <li>3. <strong>Conduct cyber risk assessment presentation</strong> - Translate technical risks to business terms</li>
                  <li>4. <strong>Establish quarterly cyber agenda item</strong> - Make security a permanent board topic</li>
                  <li>5. <strong>Bring in external expert</strong> - Third-party validation of risks and recommendations</li>
                </ol>
              </div>
            )}
            
            {boardMaturity === 'aware' && (
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-yellow-700">Board understands basics but needs deeper engagement</p>
                <p><strong>Focus Areas to Advance Maturity:</strong></p>
                <ul className="ml-4 space-y-2">
                  <li>• <strong>Tabletop exercises:</strong> Run incident simulations with board participation</li>
                  <li>• <strong>Financial impact workshops:</strong> Quantify risks in dollar terms</li>
                  <li>• <strong>Peer benchmarking:</strong> Compare your security posture to competitors</li>
                  <li>• <strong>Regulatory briefings:</strong> SEC requirements and board responsibilities</li>
                  <li>• <strong>Strategic alignment sessions:</strong> Link security to business objectives</li>
                </ul>
              </div>
            )}
            
            {boardMaturity === 'engaged' && (
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-blue-700">Board actively participates in cyber governance</p>
                <p><strong>Elevate to Champion Level:</strong></p>
                <ul className="ml-4 space-y-2">
                  <li>• <strong>Board cyber committee:</strong> Establish dedicated committee for deep dives</li>
                  <li>• <strong>Cyber-skilled director:</strong> Recruit board member with security expertise</li>
                  <li>• <strong>Regular threat briefings:</strong> Monthly intelligence updates on relevant threats</li>
                  <li>• <strong>Investment reviews:</strong> Board approval for major security investments</li>
                  <li>• <strong>KPI dashboards:</strong> Real-time security metrics available to board</li>
                </ul>
              </div>
            )}
            
            {boardMaturity === 'champion' && (
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-green-700">Maintain excellence and drive industry leadership</p>
                <p><strong>Sustaining Best Practices:</strong></p>
                <ul className="ml-4 space-y-2">
                  <li>• <strong>Thought leadership:</strong> Share your governance model with industry</li>
                  <li>• <strong>Continuous education:</strong> Board stays current on emerging threats</li>
                  <li>• <strong>M&A cyber due diligence:</strong> Board actively involved in acquisition security</li>
                  <li>• <strong>Crisis preparedness:</strong> Board regularly drills breach scenarios</li>
                  <li>• <strong>Security culture champions:</strong> Board models security-first behavior</li>
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <BookOpen className="text-blue-600" size={20} />
              Board Education Programs
            </h3>
            <div className="space-y-3 text-sm">
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-semibold mb-1">Cybersecurity 101 for Directors</p>
                <p className="text-gray-700 mb-2">90-minute session covering:</p>
                <ul className="text-gray-700 ml-4 text-xs space-y-1">
                  <li>• Current threat landscape and attack vectors</li>
                  <li>• Board fiduciary duties for cyber oversight</li>
                  <li>• SEC disclosure requirements</li>
                  <li>• Questions directors should ask</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-semibold mb-1">Tabletop Exercises</p>
                <p className="text-gray-700 mb-2">Quarterly incident simulations:</p>
                <ul className="text-gray-700 ml-4 text-xs space-y-1">
                  <li>• Ransomware attack scenario</li>
                  <li>• Data breach with regulatory implications</li>
                  <li>• Supply chain compromise</li>
                  <li>• Nation-state attack simulation</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-semibold mb-1">Industry Peer Learning</p>
                <p className="text-gray-700 mb-2">Connect with other boards:</p>
                <ul className="text-gray-700 ml-4 text-xs space-y-1">
                  <li>• Director cybersecurity consortiums</li>
                  <li>• NACD Cyber-Risk Oversight program</li>
                  <li>• Industry-specific security forums</li>
                  <li>• Peer company board exchanges</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <MessageSquare className="text-green-600" size={20} />
              Effective Board Communication
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold mb-2">Communication Best Practices:</p>
                <ul className="text-gray-700 ml-4 space-y-1">
                  <li>• <strong>Pre-read materials:</strong> Send report 1 week before meeting</li>
                  <li>• <strong>Executive summary first:</strong> Lead with bottom-line up front</li>
                  <li>• <strong>Use analogies:</strong> Translate tech concepts to business scenarios</li>
                  <li>• <strong>Show, don't just tell:</strong> Visualizations over dense text</li>
                  <li>• <strong>Comparison context:</strong> Benchmark against peers</li>
                  <li>• <strong>Clear asks:</strong> Specific decisions needed from board</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-3 rounded mt-3">
                <p className="font-semibold mb-2">Questions Board Should Ask CISO:</p>
                <ul className="text-gray-700 ml-3 text-xs space-y-1">
                  <li>1. What keeps you up at night?</li>
                  <li>2. Are we spending enough on security?</li>
                  <li>3. How do we compare to industry peers?</li>
                  <li>4. What is our biggest vulnerability?</li>
                  <li>5. Are we ready for SEC 4-day disclosure?</li>
                  <li>6. What would a breach cost us financially?</li>
                  <li>7. Do you have the authority you need?</li>
                  <li>8. How is security enabling business growth?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <Award className="text-purple-600" size={20} />
          Board Cyber Governance Framework
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h4 className="font-semibold mb-2">Board Responsibilities</h4>
            <ul className="text-gray-700 space-y-1">
              <li>• Set risk appetite and tolerance</li>
              <li>• Oversee risk management program</li>
              <li>• Ensure adequate resources</li>
              <li>• Review major incidents</li>
              <li>• Approve security strategy</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Management Responsibilities</h4>
            <ul className="text-gray-700 space-y-1">
              <li>• Implement security program</li>
              <li>• Manage day-to-day risks</li>
              <li>• Report to board regularly</li>
              <li>• Execute approved strategy</li>
              <li>• Maintain compliance</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">CISO Responsibilities</h4>
            <ul className="text-gray-700 space-y-1">
              <li>• Develop security strategy</li>
              <li>• Lead security function</li>
              <li>• Translate tech to business</li>
              <li>• Advise board and management</li>
              <li>• Drive security culture</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Board Meeting Prep Wizard Tab
  const BoardMeetingPrepTab = () => {
    const [selectedPrep, setSelectedPrep] = useState([]);

    const prepSteps = [
      {
        id: 'soc-assessment',
        title: '1. Assess Your SOC Performance',
        description: 'Evaluate your Security Operations Center maturity and capabilities',
        tool: 'Rate My SOC',
        url: 'https://ratemysoc.com/',
        icon: Shield,
        color: 'blue',
        benefits: ['Benchmark against industry standards', 'Identify capability gaps', 'Show board SOC maturity level']
      },
      {
        id: 'team-risk',
        title: '2. Evaluate Team Security Risks',
        description: 'Assess human-factor security risks across your organization',
        tool: 'Team Risk Assessment',
        url: 'https://teamrisk.securitycareers.help/',
        icon: Users,
        color: 'purple',
        benefits: ['Identify insider threat risks', 'Training needs analysis', 'Present human security posture']
      },
      {
        id: 'budget',
        title: '3. Build Your Budget Case',
        description: 'Create data-driven budget proposals with industry-aligned KPIs',
        tool: 'CISO Budget Builder',
        url: 'https://cisobudgetbuilder.com/',
        icon: DollarSign,
        color: 'green',
        benefits: ['Justify security investments', 'Align with business objectives', 'Show ROI projections']
      },
      {
        id: 'incident-readiness',
        title: '4. Verify Incident Response Readiness',
        description: 'Ensure your incident response capabilities are board-ready',
        tool: 'Incident Response Tools',
        url: 'https://incidentresponse.tools/',
        icon: AlertTriangle,
        color: 'red',
        benefits: ['Demonstrate IR preparedness', 'Test response procedures', 'Show compliance readiness']
      },
      {
        id: 'irm-maturity',
        title: '5. Assess IRM Maturity',
        description: 'Evaluate your Incident Response Management maturity level',
        tool: 'IRM Maturity Assessment',
        url: 'https://irmaturityassessment.com/',
        icon: BarChart3,
        color: 'indigo',
        benefits: ['Benchmark IR maturity', 'Identify process improvements', 'Present maturity roadmap']
      },
      {
        id: 'vendor-risk',
        title: '6. Review Vendor Risk Posture',
        description: 'Assess and report on third-party security risks',
        tool: 'Vendor Risk Assessment',
        url: 'https://vrm.cisomarketplace.services/',
        icon: Briefcase,
        color: 'orange',
        benefits: ['Identify supply chain risks', 'Show vendor due diligence', 'Present third-party risk exposure']
      }
    ];

    const toggleStep = (stepId) => {
      setSelectedPrep(prev =>
        prev.includes(stepId)
          ? prev.filter(id => id !== stepId)
          : [...prev, stepId]
      );
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-8 rounded-lg">
          <div className="flex items-start gap-4">
            <ClipboardCheck size={48} className="flex-shrink-0" />
            <div>
              <h2 className="text-3xl font-bold mb-4">Board Meeting Prep Wizard</h2>
              <p className="text-lg mb-4">
                A step-by-step guide to preparing comprehensive, data-driven board presentations
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                  <div className="text-2xl font-bold">6 Steps</div>
                  <div className="text-sm">Integrated assessment tools</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                  <div className="text-2xl font-bold">Data-Driven</div>
                  <div className="text-sm">Quantifiable metrics & KPIs</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                  <div className="text-2xl font-bold">Board-Ready</div>
                  <div className="text-sm">Executive-level insights</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Use This Wizard */}
        <div className="bg-white/90 p-6 rounded-lg shadow-sm border-l-4 border-indigo-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800 flex items-center gap-2">
            <Rocket className="text-indigo-600" />
            Why Use This Wizard?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Comprehensive Coverage:</strong> Address all critical areas boards care about</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Quantifiable Data:</strong> Present metrics, not just narratives</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Time-Efficient:</strong> Streamlined process saves hours of prep time</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Best Practices:</strong> Based on industry-leading frameworks</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Integrated Tools:</strong> Leverage CISO Marketplace micro-tools</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                <span><strong>Actionable Insights:</strong> Generate specific recommendations</span>
              </div>
            </div>
          </div>
        </div>

        {/* Preparation Steps */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-800">Your Board Meeting Preparation Checklist</h3>
          <p className="text-gray-600">
            Click each step to mark as complete and access the integrated assessment tool. Each tool provides board-ready reports and insights.
          </p>

          {prepSteps.map((step) => {
            const Icon = step.icon;
            const isCompleted = selectedPrep.includes(step.id);
            const colorClasses = {
              blue: 'from-blue-500 to-blue-600 border-blue-500',
              purple: 'from-purple-500 to-purple-600 border-purple-500',
              green: 'from-green-500 to-green-600 border-green-500',
              red: 'from-red-500 to-red-600 border-red-500',
              indigo: 'from-indigo-500 to-indigo-600 border-indigo-500',
              orange: 'from-orange-500 to-orange-600 border-orange-500'
            };

            return (
              <div
                key={step.id}
                className={`bg-white rounded-lg shadow-md border-l-4 transition-all ${
                  isCompleted ? 'border-green-500 bg-green-50' : `border-${step.color}-500`
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`bg-gradient-to-br ${colorClasses[step.color]} p-3 rounded-lg flex-shrink-0`}>
                        <Icon className="text-white" size={28} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-xl font-bold text-gray-800">{step.title}</h4>
                          {isCompleted && (
                            <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full font-semibold">
                              ✓ Completed
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 mb-3">{step.description}</p>

                        <div className="mb-4">
                          <div className="font-semibold text-gray-700 mb-2">Key Benefits:</div>
                          <ul className="space-y-1">
                            {step.benefits.map((benefit, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                <span className={`text-${step.color}-500 mt-0.5`}>→</span>
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="flex gap-3">
                          <a
                            href={step.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colorClasses[step.color]} text-white rounded-lg font-semibold hover:opacity-90 transition-opacity`}
                          >
                            <ExternalLink size={16} />
                            Launch {step.tool}
                          </a>
                          <button
                            onClick={() => toggleStep(step.id)}
                            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                              isCompleted
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress Summary */}
        <div className="bg-white/90 p-6 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4 text-gray-800">Your Preparation Progress</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Completion Status</span>
              <span>{selectedPrep.length} of {prepSteps.length} steps completed</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-4 rounded-full transition-all duration-500"
                style={{ width: `${(selectedPrep.length / prepSteps.length) * 100}%` }}
              />
            </div>
          </div>

          {selectedPrep.length === prepSteps.length && (
            <div className="bg-green-100 border border-green-300 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={32} />
                <div>
                  <div className="font-bold text-green-800 text-lg">Congratulations! You're Board-Ready!</div>
                  <div className="text-green-700">
                    You've completed all preparation steps. Review your reports from each tool and compile your executive summary.
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedPrep.length > 0 && selectedPrep.length < prepSteps.length && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <div className="text-blue-800">
                <strong>Next Steps:</strong> Complete the remaining {prepSteps.length - selectedPrep.length} assessment{prepSteps.length - selectedPrep.length !== 1 ? 's' : ''} to ensure comprehensive board preparation.
              </div>
            </div>
          )}
        </div>

        {/* Additional Tips */}
        <div className="bg-white/90 p-6 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4 text-gray-800">Board Presentation Pro Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Target className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Lead with Business Impact</div>
                  <div className="text-sm text-gray-600">Always connect security metrics to revenue, reputation, or risk reduction</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <BarChart3 className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Use Visual Data</div>
                  <div className="text-sm text-gray-600">Charts and heat maps communicate faster than bullet points</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <TrendingUp className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Show Trends Over Time</div>
                  <div className="text-sm text-gray-600">Demonstrate progress and improvement trajectories</div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Highlight Top 3 Risks</div>
                  <div className="text-sm text-gray-600">Focus on what matters most; avoid overwhelming with details</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <DollarSign className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Tie Requests to ROI</div>
                  <div className="text-sm text-gray-600">Every budget ask should show clear return or risk mitigation</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MessageSquare className="text-indigo-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <div className="font-semibold text-gray-800">Avoid Technical Jargon</div>
                  <div className="text-sm text-gray-600">Translate tech speak into business language the board understands</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Template Content Data
  const templateData = {
    boardReport: `QUARTERLY CYBERSECURITY BOARD REPORT
[Quarter & Year]

EXECUTIVE SUMMARY
• Current Security Posture: [Rating: Strong/Moderate/Needs Improvement]
• Key Achievements: [3-5 bullet points of major wins]
• Top 3 Risks: [Critical risks requiring board attention]
• Budget Status: [% of annual budget utilized]
• Regulatory Compliance: [Status of key requirements]

SECURITY METRICS DASHBOARD
Operational Metrics:
• Mean Time to Detect (MTTD): [X hours/days]
• Mean Time to Respond (MTTR): [X hours/days]
• Security Incidents: [Number] ([+/-]% vs last quarter)

Risk Metrics:
• Critical Vulnerabilities Outstanding: [Number]
• Third-Party Risk Score: [Rating/Score]
• Employee Security Training Completion: [X%]

TOP CYBERSECURITY RISKS
1. [Risk Name]
   Impact: [High/Medium/Low] | Likelihood: [High/Medium/Low]
   Business Impact: [Revenue/Reputation/Compliance impact]
   Mitigation Status: [In Progress/Planned/Complete]
   Budget Required: [$X]

COMPLIANCE & REGULATORY UPDATE
• SEC Cybersecurity Rules: [Status and any material incidents]
• Industry Regulations: [HIPAA/PCI-DSS/SOX status]
• Audit Findings: [Open items and remediation timeline]

BUDGET REQUESTS
• Current Quarter Utilization: [X%]
• Upcoming Needs:
  1. [Item]: [$X] - [Business justification]
  2. [Item]: [$X] - [Business justification]

Prepared by: [CISO Name]
Date: [Date]`,

    incidentResponse: `CYBERSECURITY INCIDENT RESPONSE PLAN
Board Communication Template

INITIAL NOTIFICATION (Within 1 Hour)

To: Board of Directors / Audit Committee
From: [CISO Name]
Subject: URGENT - Cybersecurity Incident Notification
Severity: [CRITICAL / HIGH / MEDIUM]

INCIDENT SUMMARY:
• Discovery Date/Time: [Timestamp]
• Incident Type: [Ransomware/Data Breach/DDoS/Insider Threat/etc.]
• Current Status: [Contained/In Progress/Under Investigation]
• Estimated Impact: [Preliminary assessment]

IMMEDIATE ACTIONS TAKEN:
1. [Action taken]
2. [Action taken]
3. [Action taken]

INCIDENT RESPONSE TEAM ACTIVATED:
• Incident Commander: [Name]
• Technical Lead: [Name]
• Legal Counsel: [Name/Firm]
• Communications: [Name]

PRELIMINARY ASSESSMENT:
• Systems Affected: [Description]
• Data at Risk: [Type and estimated volume]
• Business Operations Impact: [Description]
• Customer Impact: [Yes/No - Description]
• Regulatory Implications: [SEC/HIPAA/PCI-DSS/etc.]

SEC DISCLOSURE ASSESSMENT:
• Materiality Determination: [Process and conclusion]
• 8-K Filing Requirement: [Yes/No]
• Filing Deadline: [Date if applicable]

NEXT STEPS:
• Containment Strategy: [Description]
• Investigation Timeline: [Estimate]
• Next Board Update: [Timeframe]

Contact: [CISO Name, Phone, Email]`,

    budgetJustification: `CYBERSECURITY BUDGET JUSTIFICATION
[Fiscal Year]

EXECUTIVE SUMMARY
• Total Budget Request: [$X]
• Increase Over Prior Year: [X% / $Y]
• Risk Reduction Value: [$X estimated]
• Regulatory Drivers: [SEC/NIS2/Other requirements]

BUDGET REQUEST BREAKDOWN

People (X% of budget):
• Current Team: [X FTEs]
• Requested Additions: [Y FTEs]
• Cost: [$X]
• Justification: [Current gaps and risks]

Technology (X% of budget):
• New/Renewal Investments:
  - [Tool/Service]: [$X] - [Business value]
  - [Tool/Service]: [$X] - [Business value]
• Total: [$X]

BUSINESS JUSTIFICATION

Risk Reduction:
Without Investment:
• Probability of Material Incident: [X%]
• Estimated Cost of Breach: [$X million]
• Expected Annual Loss: [$Y]

With Investment:
• Reduced Incident Probability: [X%]
• Risk Reduction Value: [$Z]
• ROI: [Calculation]

Regulatory Compliance:
• SEC Disclosure Requirements: [Costs of non-compliance]
• Industry Regulations: [HIPAA/PCI-DSS penalties]
• Compliance Investment: [$X] vs. Penalty Risk: [$Y]

COST OF DOING NOTHING

Quantified Risks:
1. Data Breach: [$X million average cost]
2. Ransomware: [$Y average cost]
3. Regulatory Fines: [$Z potential]

Total Expected Loss Without Investment: [$X]
Proposed Security Investment: [$Y]
Net Risk Reduction: [$Z]

RECOMMENDATION
Approve investment of [$X]

Prepared by: [CISO Name]
Date: [Date]`,

    metricsKPI: `BOARD CYBERSECURITY METRICS DASHBOARD
[Month/Quarter]

KEY PERFORMANCE INDICATORS (KPIs)

DETECTION & RESPONSE
• Mean Time to Detect (MTTD): [X] hours (Target: <24)
• Mean Time to Respond (MTTR): [X] hours (Target: <48)
• Trend: ↑/↓ [X%] vs last quarter

VULNERABILITY MANAGEMENT
• Critical Vulnerabilities Open: [X]
• Patch Compliance Rate: [X%] (Target: >95%)
• Avg Time to Patch: [Z] days (Target: <30)

SECURITY INCIDENTS
• Total Incidents This Period: [X]
• vs Last Period: [+/-Y%]
• Material Incidents (SEC Reportable): [Z]

HUMAN FACTOR SECURITY
• Security Training Completion: [X%] (Target: 100%)
• Phishing Click Rate: [X%] (Target: <5%)
• MFA Adoption Rate: [X%] (Target: >95%)

FINANCIAL METRICS
• Security Spend as % of IT Budget: [X%]
• Cost Per Incident: [$X]
• Cyber Insurance Coverage: [$X million]

TOP 5 RISKS REQUIRING BOARD ATTENTION

1. [Risk Name]
   Impact: $[X] | Likelihood: [Y%]
   Mitigation: [Status]

2. [Risk Name]
   Impact: $[X] | Likelihood: [Y%]
   Mitigation: [Status]

EXECUTIVE SUMMARY FOR BOARD

Overall Security Posture: [Strong/Moderate/Needs Improvement]

Key Strengths:
• [Metric/Area performing well]
• [Metric/Area performing well]

Areas for Improvement:
• [Metric/Area needing attention]
• [Metric/Area needing attention]

Prepared by: [CISO Name]`,

    riskRegister: `CYBERSECURITY RISK REGISTER
[Current Date]

RISK SCORING METHODOLOGY
Impact Scale (1-5): 1=Minimal, 5=Critical
Likelihood Scale (1-5): 1=Rare, 5=Almost Certain
Risk Score = Impact × Likelihood

SAMPLE RISK: RANSOMWARE ATTACK

Description:
Malicious actors deploy ransomware across critical systems

Business Impact:
• Revenue: $5-20M (based on 3-14 day outage)
• Operations: Complete business disruption
• Reputation: Customer trust erosion
• Compliance: Potential SEC 8-K filing
• Legal: Class action liability, regulatory fines

Current Controls:
• Endpoint Detection & Response (EDR)
• Daily backups with 7-day retention
• Network segmentation (partial)
• Email filtering and anti-phishing
• Security awareness training
• Incident response plan

Inherent Risk:
• Impact: 5 (Critical)
• Likelihood: 4 (Likely - 60%)
• Score: 20 (High Risk)

Residual Risk:
• Impact: 4 (High)
• Likelihood: 3 (Possible - 30%)
• Score: 12 (Medium Risk)

Mitigation Actions:
1. Implement immutable backups: Q2 | $150K
2. Complete network segmentation: Q3 | $200K
3. Deploy advanced threat detection: Q2 | $100K
4. Tabletop exercise with board: Q2 | $25K

Target Residual Risk:
• Impact: 3 (Moderate)
• Likelihood: 2 (Unlikely - 15%)
• Score: 6 (Low Risk)
• Target Date: Q4 2026

RISK SUMMARY
High Risks (Score 15-25): [X] risks
Medium Risks (Score 6-12): [Y] risks
Low Risks (Score 1-5): [Z] risks

Prepared by: [CISO Name]
Date: [Date]`,

    sec8k: `SEC FORM 8-K ITEM 1.05
Cybersecurity Incident Disclosure Template
(Material Incident Reporting - Due Within 4 Business Days)

COMPANY INFORMATION
Registrant Name: [Company Legal Name]
Commission File Number: [SEC File Number]
IRS Employer ID: [EIN]
Date of Report: [Date of this 8-K filing]

ITEM 1.05 - MATERIAL CYBERSECURITY INCIDENTS

MATERIALITY DETERMINATION

Date Incident Discovered: [Date]
Date Materiality Determined: [Date]
Days to Determination: [X days]

Materiality Assessment Process:
The Company's Incident Response Team, in consultation with the CISO,
General Counsel, CFO, and external legal counsel, assessed the materiality
of this incident based on:

• Quantitative Impact:
  - Financial impact: [$X estimated]
  - Customer accounts affected: [Number or %]
  - Revenue at risk: [$Y]
  - Remediation costs: [$Z estimated]

• Qualitative Impact:
  - Reputational harm: [Assessment]
  - Regulatory exposure: [Assessment]
  - Competitive disadvantage: [Assessment]
  - Operational disruption: [Assessment]

Conclusion: Based on these factors, the Company has determined this
incident to be material to investors.

INCIDENT DESCRIPTION

Nature of the Incident:
On [Date], the Company discovered [type of incident - e.g., unauthorized
access to systems, ransomware attack, data breach]. The incident involved
[brief technical description in business terms].

Timing:
• Initial Detection: [Date and time]
• Incident Began (estimated): [Date and time]
• Containment Achieved: [Date and time, or "ongoing"]
• Investigation Status: [Ongoing/Preliminary findings/Complete]

Scope and Impact:
• Systems Affected: [Description - avoid overly technical details]
• Data Compromised: [Type of data - PII, financial, intellectual property, etc.]
• Volume: [Estimated number of records/customers affected]
• Business Operations: [Description of operational impact]
• Customer Impact: [Description of impact on customers/users]

Material Impact or Reasonably Likely Material Impact:

Financial Impact:
• Direct Costs: [$X] - [forensics, legal, remediation, notification costs]
• Business Interruption: [$Y] - [estimated revenue loss]
• Potential Liability: [$Z range] - [regulatory fines, litigation]
• Insurance Coverage: [$W] - [expected recovery amount]
• Net Expected Impact: [$___]

Operational Impact:
[Description of how operations have been or may be affected]

Customer/Partner Impact:
[Description of impact on customers, suppliers, or partners]

Regulatory Impact:
[Description of regulatory notifications required, potential fines]

Reputational Impact:
[Description of potential brand/trust implications]

RESPONSE AND REMEDIATION

Immediate Actions Taken:
1. [Action taken - e.g., isolated affected systems]
2. [Action taken - e.g., engaged forensics firm]
3. [Action taken - e.g., notified law enforcement]
4. [Action taken - e.g., activated incident response plan]

Ongoing Response:
• Investigation: [Forensics firm name] has been engaged to conduct
  a comprehensive investigation
• Law Enforcement: [FBI/Secret Service/State AG] has been notified
• Customer Notification: [Plan and timeline]
• Regulatory Notifications: [Agencies and timeline]
• Credit Monitoring: [Services being offered to affected individuals]

Remediation Measures:
1. [Technical control implemented]
2. [Process improvement implemented]
3. [Additional security measures planned]

Current Status:
As of [Date], the incident is [contained/under investigation/remediated].
The Company [has/has not] experienced any additional related incidents.

UNCERTAINTIES AND RISK FACTORS

The Company is continuing to assess the full scope and impact of this
incident. The following uncertainties remain:

• [Uncertainty 1 - e.g., full extent of data accessed is still being determined]
• [Uncertainty 2 - e.g., potential for additional regulatory actions]
• [Uncertainty 3 - e.g., possibility of class action litigation]
• [Uncertainty 4 - e.g., long-term customer trust impact]

The actual costs and impacts may differ materially from current estimates
as the investigation progresses.

FORWARD-LOOKING STATEMENTS
This disclosure contains forward-looking statements regarding the expected
impact of the cybersecurity incident. Actual results may differ materially
based on factors including the outcome of ongoing investigations, regulatory
actions, litigation, and effectiveness of remediation efforts.

ADDITIONAL INFORMATION
The Company will provide updates in future SEC filings as material new
information becomes available.

────────────────────────────────────────────────────

MATERIALITY ASSESSMENT CHECKLIST
(Internal Use - Do Not Include in 8-K Filing)

□ Financial Impact Assessment
  □ Incident response costs >$[threshold]
  □ Revenue impact >$[threshold]
  □ Cost exceeds [X%] of quarterly revenue

□ Operational Impact Assessment
  □ Critical systems offline >24 hours
  □ Revenue-generating operations disrupted
  □ Supply chain disruption

□ Data Breach Assessment
  □ Customer PII compromised (>$X threshold or >Y customers)
  □ Financial data compromised
  □ Intellectual property stolen
  □ Healthcare data (HIPAA breach)

□ Regulatory Assessment
  □ Likely regulatory fines >$[threshold]
  □ License/certification at risk
  □ Multiple state notification laws triggered

□ Reputational Assessment
  □ Likely media coverage
  □ Customer trust significantly impacted
  □ Competitive advantage lost

□ Legal Assessment
  □ Class action risk
  □ Contract breaches
  □ Third-party claims likely

If 2+ categories checked → Likely Material
If any single category has severe impact → Likely Material

FILING TIMELINE CALCULATOR
Discovery Date: [Date]
Day 1 (Discovery): [Date]
Day 2: [Date]
Day 3: [Date]
Day 4 (Filing Deadline): [Date] ⚠️

Note: Filing deadline may be delayed only if delay is necessary to protect
national security or public safety, as determined by the Attorney General.

Prepared by: [CISO Name]
Reviewed by: [General Counsel Name]
Approved by: [CEO Name]
Date: [Date]`,

    tabletopExercise: `CYBERSECURITY TABLETOP EXERCISE
Planning and Execution Guide

HOW TO CREATE A TABLETOP EXERCISE FOR YOUR COMPANY

PURPOSE OF TABLETOP EXERCISES
• Test incident response procedures in a low-risk environment
• Identify gaps in plans, tools, and team preparedness
• Build muscle memory for crisis decision-making
• Educate board and executives on cyber risk scenarios
• Meet compliance requirements (cyber insurance, regulations)
• Strengthen cross-functional collaboration

EXERCISE PLANNING (4-6 WEEKS BEFORE)

Step 1: Define Objectives
Choose your primary goal:
□ Test incident response plan effectiveness
□ Educate board/executives on cyber risks
□ Validate communication procedures
□ Assess vendor/third-party coordination
□ Meet regulatory/insurance requirements

Step 2: Select Scenario
Choose a realistic threat for your organization:
□ Ransomware attack with data exfiltration
□ Business email compromise (BEC) / Wire fraud
□ Supply chain compromise via vendor
□ Insider threat / Data theft
□ DDoS attack impacting customer services
□ Cloud security breach
□ Physical security breach leading to cyber incident

Step 3: Identify Participants
Essential Participants:
□ CEO or designated executive sponsor
□ Board members (or Audit Committee)
□ CISO / IT Security Lead
□ CIO / IT Director
□ General Counsel / Legal team
□ CFO / Finance representative
□ Communications / PR lead
□ HR representative
□ Key business unit leaders
□ External advisors (optional): Legal counsel, IR firm, insurer

Roles to Assign:
• Facilitator: Guides exercise, asks questions
• Scribe: Documents decisions and gaps
• Timekeeper: Manages scenario progression
• Subject Matter Experts: Provide technical context

Step 4: Prepare Materials
Create These Documents:
1. Scenario Overview (1-2 pages)
2. Injects Timeline (events that unfold during exercise)
3. Discussion Questions
4. Participant Guide
5. Evaluation Form
6. Gap Analysis Template

SAMPLE TABLETOP EXERCISE STRUCTURE
────────────────────────────────────────────

SCENARIO: Ransomware Attack with Data Exfiltration

Exercise Duration: 90-120 minutes

OPENING (10 minutes)
• Welcome and introductions
• Exercise objectives and ground rules
• Scenario overview presentation
• Questions and clarifications

GROUND RULES:
• This is a discussion-based exercise, not a technical drill
• No-fault environment - identify gaps, not blame people
• Focus on decision-making processes, not technical details
• Stay in character - respond as you would in a real incident
• Cell phones on silent; this is a simulation but treat it seriously

PHASE 1: INITIAL DETECTION (20 minutes)

INJECT 1 - Monday, 6:45 AM
Your IT monitoring team alerts the CISO: Multiple servers are showing
encryption activity. Users in the finance department cannot access files.
A ransom note appears on several workstations demanding 50 Bitcoin
(approximately $2M USD) for decryption keys.

DISCUSSION QUESTIONS:
• Who needs to be notified immediately? In what order?
• What immediate actions should IT take?
• Should we pay the ransom? Who decides?
• What external parties should we engage? (Legal, forensics, FBI, insurance)
• How do we determine the scope of the incident?

[Facilitator: Allow 15 minutes discussion, then present next inject]

INJECT 2 - Monday, 8:30 AM
Initial assessment reveals:
• 30% of corporate servers encrypted
• Backups for critical systems exist but are 3 days old
• The ransomware note includes proof they exfiltrated data
• Customer database accessed (2.5M customer records including PII)
• Attackers threaten to publish data on dark web if not paid

DISCUSSION QUESTIONS:
• Does this change our response strategy?
• What are our legal obligations? (SEC 8-K, state breach laws, GDPR)
• What do we tell customers? When?
• What do we tell the board? Shareholders? Media?
• Should we notify law enforcement now?
• Is this a material event requiring SEC disclosure?

PHASE 2: CONTAINMENT & COMMUNICATION (25 minutes)

INJECT 3 - Monday, 2:00 PM
Updates from investigation:
• Initial access via compromised VPN credentials (no MFA)
• Attacker had access for 14 days before deploying ransomware
• Additional backdoors discovered in environment
• Forensics firm estimates 7-14 days for full remediation
• Operations at 40% capacity; revenue-generating systems impacted
• Customer support calls increasing; social media questions appearing

DISCUSSION QUESTIONS:
• How do we communicate internally? To employees? To customers?
• What's our external communication strategy?
• Who is the spokesperson?
• What can we say publicly while investigation is ongoing?
• How do we manage business continuity?
• What are the financial implications? Should we update guidance?

INJECT 4 - Tuesday, 9:00 AM
New developments:
• Media outlet contacts PR team - they received a tip about the breach
• Cyber insurance carrier requests detailed incident report
• SEC inquiry email received asking about the incident
• Class action law firm announces investigation
• Three major customers asking for security certifications

DISCUSSION QUESTIONS:
• How do we respond to media inquiries?
• What information do we share with insurance?
• How do we handle SEC inquiry?
• What do we tell customers asking about certifications?
• Should we consider third-party security audits post-incident?

PHASE 3: RECOVERY & LESSONS LEARNED (20 minutes)

INJECT 5 - One Week Later
Situation update:
• Systems 80% restored from backups
• Customer notifications completed (required by law in 47 states)
• Estimated costs: $5M direct + $10M potential lost revenue
• 12 customer contracts cancelled citing security concerns
• Stock price down 8% since incident disclosure

DISCUSSION QUESTIONS:
• What could we have done differently?
• What investments should we make to prevent this?
• How do we rebuild customer trust?
• What board oversight changes are needed?
• What metrics should we track going forward?

DEBRIEF & GAP ANALYSIS (20 minutes)

Facilitator leads discussion:
• What went well during this exercise?
• What gaps did we identify?
• What decisions were unclear or difficult?
• What resources/information were missing?
• What should we do differently in a real incident?

GAP CATEGORIES TO ASSESS:
□ Incident Response Plan adequacy
□ Communication protocols (internal/external)
□ Decision-making authority and escalation
□ Technical capabilities and tools
□ Vendor/partner coordination
□ Legal and regulatory compliance knowledge
□ Insurance coverage understanding
□ Business continuity planning
□ Board reporting and governance

WRAP-UP (10 minutes)
• Thank participants
• Collect evaluation forms
• Outline next steps and action items
• Schedule follow-up meeting to review findings

POST-EXERCISE ACTIONS

Within 1 Week:
□ Compile exercise findings and gap analysis
□ Draft after-action report
□ Create prioritized remediation plan
□ Assign owners to action items

Within 1 Month:
□ Present findings to senior leadership
□ Update incident response plans
□ Address high-priority gaps
□ Schedule training for identified needs

Within 3-6 Months:
□ Implement medium-priority improvements
□ Conduct follow-up mini-exercise
□ Report progress to board
□ Plan next full tabletop exercise

EXERCISE VARIATIONS FOR DIFFERENT AUDIENCES

Board-Level Exercise (60-90 min):
• Focus on governance and oversight questions
• Emphasize business impact and decision-making
• Less technical detail, more strategic focus
• Include SEC disclosure and shareholder communication

Executive Leadership Exercise (90-120 min):
• Balance technical and business decisions
• Focus on crisis management and coordination
• Include cross-functional communication challenges

Technical Team Exercise (2-4 hours):
• Detailed technical response procedures
• Tool usage and playbook validation
• Hands-on components (optional)

TIPS FOR SUCCESS

DO:
✓ Make scenario realistic but not overly complex
✓ Allow for open discussion and debate
✓ Focus on process, not perfection
✓ Document all gaps and decisions
✓ Follow through on action items

DON'T:
✗ Turn it into a blame session
✗ Make it overly technical for non-technical audience
✗ Skip the after-action report
✗ Forget to schedule follow-ups
✗ Conduct only once - make it regular (annually minimum)

QUESTIONS TO ALWAYS INCLUDE

Decision-Making:
• Who has authority to make this decision?
• What information do we need to decide?
• What's our timeline for deciding?

Communication:
• Who needs to be informed?
• What can we say publicly?
• Who is authorized to speak?

Legal/Regulatory:
• What are our legal obligations?
• What regulatory deadlines apply?
• What external notifications are required?

Business Impact:
• How does this affect operations?
• What's the financial impact?
• How do we maintain business continuity?

Technical Response:
• What immediate technical actions are needed?
• Who is responsible for each action?
• What external help do we need?

EVALUATION METRICS

Measure Exercise Success:
• Participant engagement level
• Number of gaps identified
• Clarity of decision-making
• Action items generated
• Follow-through on improvements

SAMPLE ANNUAL EXERCISE SCHEDULE

Q1: Business Email Compromise (BEC) scenario
Q2: Supply chain/third-party breach scenario
Q3: Ransomware with data exfiltration scenario
Q4: Insider threat scenario

Prepared by: [CISO Name]
Date: [Date]`,

    annualTraining: `YEAR-END CYBERSECURITY AWARENESS TRAINING
Annual Program Template

PROGRAM OVERVIEW

Purpose: Annual comprehensive security awareness training for all employees
Target Audience: All staff (customize sections by role)
Duration: 45-60 minutes
Delivery Method: [In-person / Virtual / E-learning module]
Completion Deadline: [Date]
Compliance: [REQUIRED for cyber insurance, NIST compliance, etc.]

────────────────────────────────────────────

MODULE 1: WHY SECURITY MATTERS (5 minutes)

Opening Message from [CEO/CISO]:
"Cybersecurity isn't just IT's job - it's everyone's responsibility. This year:
• [X]% of breaches started with human error
• Our industry saw [Y]% increase in attacks
• Each of you is a critical defense layer
• This training helps protect our company, customers, and your personal data"

Year in Review:
• [Company name] security wins this year
• Industry threats that affected companies like ours
• Why attackers target employees, not just technology
• Real cost of security incidents: [$X average breach cost]

Your Role:
• You are the human firewall
• Most attacks start with people, not technology
• Security is part of your job description
• When in doubt, ask - it's always the right thing to do

────────────────────────────────────────────

MODULE 2: PHISHING & EMAIL SECURITY (10 minutes)

What is Phishing?
• Fraudulent emails designed to steal credentials or deliver malware
• #1 method attackers use to breach companies
• Getting more sophisticated with AI

Red Flags to Watch For:
□ Urgent language ("ACT NOW" / "ACCOUNT SUSPENDED")
□ Unexpected attachments or links
□ Requests for passwords or financial information
□ Sender email doesn't match claimed identity
□ Generic greetings ("Dear User" instead of your name)
□ Poor grammar or spelling
□ Unusual requests from executives (verify via different channel)
□ Links that don't match the displayed text (hover to check)

Real Examples from This Year:
[Include 2-3 actual phishing emails your company received, with explanations]

Example 1: Fake HR Email
Subject: "ACTION REQUIRED: Update Your Benefits"
Red Flags: [Point out the suspicious elements]

Example 2: Executive Impersonation
Subject: "Urgent Wire Transfer Needed"
Red Flags: [Point out the suspicious elements]

What To Do:
✓ Hover over links before clicking
✓ Verify requests via separate channel (call/Slack/in person)
✓ Use "Report Phishing" button in email client
✓ When in doubt, forward to security@[company].com
✓ Never share passwords, even if email looks legitimate

Business Email Compromise (BEC):
• Attackers impersonate executives to request wire transfers
• Average loss: $120,000 per incident
• ALWAYS verify payment requests via phone call
• Use established procedures for financial transactions

Phishing Simulation Program:
• We send test phishing emails quarterly
• This is training, not punishment
• If you click: expect immediate micro-training
• Report rate is a positive metric we track

────────────────────────────────────────────

MODULE 3: PASSWORD & AUTHENTICATION SECURITY (8 minutes)

Password Best Practices:
✓ Use password manager: [Company-approved tool]
✓ Minimum 12 characters (longer is stronger)
✓ Unique password for every account
✓ Enable MFA/2FA everywhere possible
✗ Never reuse passwords across sites
✗ Never share passwords (even with IT)
✗ Avoid simple patterns (password123, Summer2024!)

Multi-Factor Authentication (MFA):
• Required for: [List company systems]
• Why it matters: Stops 99.9% of account takeover attacks
• Types: Authenticator app (best), SMS (better than nothing), hardware keys
• Setup help: [Link to IT instructions]

Common Password Mistakes:
• Using company name + year (Acme2024!)
• Pet names + birth year (Fluffy1985!)
• Keyboard patterns (qwerty123)
• Sharing passwords on sticky notes or documents

What To Do If Compromised:
1. Change password immediately
2. Report to IT Security: security@[company].com
3. Check what accounts used that password
4. Enable MFA if not already enabled
5. Monitor account for unusual activity

────────────────────────────────────────────

MODULE 4: DEVICE & DATA SECURITY (8 minutes)

Laptop & Mobile Security:
□ Keep devices locked when not in use (auto-lock: 5 min)
□ Never leave devices unattended in public
□ Report lost/stolen devices to IT immediately
□ Keep software and OS updated (enable auto-updates)
□ Only install company-approved software
□ Encrypt devices (required for company data)

Working Remotely:
✓ Use company VPN when accessing work resources
✓ Secure home WiFi with WPA2/WPA3 encryption
✓ Create separate WiFi network for guests/IoT devices
✓ Be aware of shoulder surfing in public spaces
✗ Never use public WiFi without VPN
✗ Don't mix personal and work on same device (if BYOD prohibited)

Data Handling:
• Customer data = crown jewels - handle with care
• Classification levels:
  - Public: Can be freely shared
  - Internal: For employees only
  - Confidential: Restricted access, encryption required
  - Restricted: Highest security, board/exec level

□ Email confidential data only to authorized recipients
□ Use secure file sharing: [Company tool]
□ Never store customer data on personal devices
□ Shred or securely delete sensitive documents
□ Lock screens when leaving desk
□ Clear desk policy: No sensitive documents visible

Physical Security:
□ Wear badge visibly at all times
□ Don't tailgate / Don't allow tailgating
□ Challenge unknown visitors politely
□ Report suspicious behavior to security
□ Secure documents before leaving office
□ Dispose of documents properly (shredder)

────────────────────────────────────────────

MODULE 5: CLOUD & APPLICATION SECURITY (7 minutes)

Approved Tools:
Use only IT-approved applications for work:
• File sharing: [Approved tools]
• Communication: [Approved tools]
• Password management: [Approved tools]
• Collaboration: [Approved tools]

Shadow IT Risks:
• Using unauthorized cloud services creates security gaps
• Data may not be backed up or protected
• Company loses visibility and control
• Violates compliance requirements
• When you need a tool: submit request to IT

Cloud Best Practices:
□ Check sharing settings before uploading files
□ Use company accounts, not personal accounts
□ Review access permissions regularly
□ Don't share login credentials
□ Report unauthorized access attempts

Social Media & Online Presence:
✗ Don't overshare company information on social media
✗ Don't post internal documents or screenshots
✗ Be cautious accepting connection requests from unknown people
✗ Don't discuss clients or projects publicly
✓ Think before you post - it represents the company
✓ Report social engineering attempts via LinkedIn/Twitter

────────────────────────────────────────────

MODULE 6: INCIDENT RESPONSE & REPORTING (7 minutes)

What is a Security Incident?
• Suspected phishing email you clicked
• Lost or stolen device
• Malware/ransomware alert
• Unauthorized access to accounts
• Data breach or exposure
• Suspicious system behavior
• Social engineering attempt

How to Report:
• Email: security@[company].com
• Phone: [Security hotline]
• Slack: #security-incidents
• In person: Contact [CISO name] or IT team

What to Include in Report:
• What happened (be specific)
• When it occurred
• What systems/data were affected
• What actions you've taken
• Your contact information

Response Time:
• We triage all reports within [X] hours
• Critical incidents: immediate response
• You'll receive acknowledgment and guidance
• Follow-up investigation may be needed

No Punishment for Reporting:
• We have a no-blame culture for security
• Early reporting minimizes damage
• Hiding incidents makes them worse
• "If you see something, say something"

When NOT to Wait:
Report these IMMEDIATELY (don't wait until end of day):
• Ransomware message appeared
• Lost device with company data
• Clicked link and entered credentials
• Unauthorized money transfer request
• Customer data potentially exposed

────────────────────────────────────────────

MODULE 7: EMERGING THREATS (5 minutes)

AI-Powered Attacks:
• Deepfake voice/video of executives
• AI-generated phishing (perfect grammar, personalized)
• Automated social engineering at scale
• How to defend: Verify via multiple channels

Ransomware Evolution:
• Now includes data extortion ("pay or we publish")
• Targets backups specifically
• Attacks supply chains and vendors
• Prevention: Vigilance + MFA + updates

QR Code Phishing ("Quishing"):
• Malicious QR codes in emails or physical locations
• Bypasses email security filters
• Verify QR codes before scanning
• Company policy: [Your policy on QR codes]

Supply Chain Attacks:
• Attackers compromise vendors to reach us
• Verify vendor communications independently
• Report suspicious vendor emails
• Question unexpected software updates

────────────────────────────────────────────

MODULE 8: ROLE-SPECIFIC RESPONSIBILITIES (5 minutes)

For Managers/Leaders:
• Model good security behavior for your team
• Don't pressure staff to bypass security controls
• Support team members who report incidents
• Include security in team discussions
• Approve security tool requests promptly

For Finance/Accounting:
• Extra vigilance on payment requests
• Always verify via phone (not email/text)
• Use established payment approval workflows
• Be skeptical of urgency in financial requests
• Know your authority limits

For HR:
• Protect employee PII like customer data
• Verify identity before sharing employee info
• Secure onboarding/offboarding processes
• Training records = compliance evidence

For Developers/Engineers:
• Follow secure coding practices
• Code reviews include security checks
• Don't hardcode credentials
• Use approved libraries and frameworks
• Report vulnerabilities you discover

For Sales/Marketing:
• Protect customer lists and prospect data
• Secure CRM and marketing automation tools
• Verify partner requests for data
• Be cautious with demo/trial accounts

For Everyone:
• Security is part of your job
• Protect customer data as your own
• Question suspicious requests
• Report incidents immediately
• Ask security team if unsure

────────────────────────────────────────────

MODULE 9: COMPLIANCE & POLICY (5 minutes)

Key Policies You Must Follow:
□ Acceptable Use Policy
□ Data Classification & Handling Policy
□ Password & Authentication Policy
□ Bring Your Own Device (BYOD) Policy
□ Incident Response Policy
□ Remote Work Security Policy

Where to Find Policies:
[Link to company policy portal]

Policy Violations:
• Can result in disciplinary action
• May expose company to legal/regulatory risk
• Could void cyber insurance coverage
• Protect yourself and the company by following policies

Regulatory Requirements:
Our industry compliance obligations:
• [GDPR / CCPA / HIPAA / PCI-DSS / SOX / etc.]
• Your role in compliance: [Specific requirements]
• Audits and assessments
• Penalties for non-compliance: [Examples]

Cyber Insurance Requirements:
Our insurance requires:
□ Annual security training (this training!)
□ MFA on all accounts
□ Regular backups
□ Incident response plan
□ Regular security assessments

────────────────────────────────────────────

MODULE 10: RESOURCES & SUPPORT (3 minutes)

Security Team Contact:
• Email: security@[company].com
• Phone: [Number]
• Slack: #security
• Office: [Location]

Quick Reference:
• Report incident: [Method]
• Password reset: [Link/process]
• MFA setup help: [Link]
• Security policies: [Link]
• Approved software list: [Link]

Monthly Security Newsletter:
• Subscribe: [Link]
• Tips, threats, and company updates
• Case studies and lessons learned

Office Hours:
• Security team office hours: [Day/Time]
• Drop in with questions
• No question is too basic

Additional Training:
• Role-specific security training: [Available modules]
• Advanced security certification: [If applicable]
• Lunch & Learn sessions: [Schedule]

────────────────────────────────────────────

KNOWLEDGE CHECK & CERTIFICATION

Quiz (10 questions):
1. What should you do if you receive a suspicious email?
2. What is the minimum password length?
3. True/False: It's okay to share your password with your manager
4. What should you do immediately if you lose your laptop?
5. Which tool should you use for file sharing?
6. What does MFA stand for and why is it important?
7. How quickly should you report a security incident?
8. What are 3 red flags of a phishing email?
9. Is it okay to use public WiFi for work without VPN?
10. Who should you contact to report a security concern?

Passing Score: 80% (8 out of 10)
Retake: Available immediately if needed

CERTIFICATION

By completing this training, I acknowledge that I:
□ Understand my role in protecting company and customer data
□ Will follow company security policies and procedures
□ Will report security incidents immediately
□ Will use only approved tools and applications for work
□ Know how to identify and report phishing attempts
□ Understand the consequences of security policy violations

Employee Signature: __________________ Date: __________

Training Completion Recorded: [System tracks automatically]
Certificate Issued: [Date]
Valid Through: [Date] (Annual renewal required)

────────────────────────────────────────────

THANK YOU!

Remember: Security starts with YOU.

Questions? Contact security@[company].com anytime.

Stay vigilant. Stay secure.

[CISO Name]
Chief Information Security Officer
[Company Name]`,

    boardCharter: `BOARD CYBERSECURITY OVERSIGHT CHARTER
Establishing Board Governance Framework

PURPOSE

This Charter establishes the Board of Directors' oversight responsibilities for
cybersecurity risk management at [Company Name]. It defines roles, responsibilities,
and processes to ensure effective governance of information security and cyber risk.

SCOPE

This Charter applies to:
• Board of Directors
• Audit Committee / Risk Committee / Technology Committee
• Executive Management
• Chief Information Security Officer (CISO)

BOARD'S ROLE IN CYBERSECURITY

The Board recognizes cybersecurity as a critical business risk requiring active
oversight and governance. The Board's role includes:

Strategic Oversight:
• Approve cybersecurity strategy aligned with business objectives
• Ensure adequate resources and budget for security program
• Review and approve risk appetite and tolerance levels
• Monitor industry trends and emerging threats

Risk Management:
• Review enterprise cybersecurity risk assessments
• Understand top cyber risks and mitigation strategies
• Approve risk acceptance for high-severity vulnerabilities
• Ensure alignment with Enterprise Risk Management (ERM)

Compliance & Regulatory:
• Oversee compliance with cybersecurity regulations (SEC, GDPR, NIS2, etc.)
• Review material incident disclosures and SEC 8-K filings
• Ensure appropriate cyber insurance coverage
• Monitor audit findings and remediation

Incident Oversight:
• Receive prompt notification of material security incidents
• Oversee incident response and crisis management
• Review post-incident analysis and lessons learned
• Ensure business continuity and disaster recovery plans

Performance Monitoring:
• Review cybersecurity metrics and KPIs quarterly
• Assess effectiveness of security controls and investments
• Evaluate CISO and security team performance
• Benchmark against industry peers

────────────────────────────────────────────

COMMITTEE STRUCTURE

Primary Oversight Committee: [Audit / Risk / Technology Committee]

Committee Responsibilities:
□ Meet quarterly (minimum) to review cybersecurity matters
□ Receive detailed briefings from CISO
□ Review security budget and investments
□ Oversee third-party security assessments
□ Report material matters to full Board

Committee Composition:
• Minimum [3-5] members
• At least one member with cybersecurity expertise (recommended)
• Chair: [Role/Name]
• Technology/Security expertise: [Name(s)]

Recommended: Designate a "Cybersecurity Champion" board member with:
• Technology or security background
• Available for CISO consultation between meetings
• Liaison between CISO and full Board

Full Board Involvement:
• Annual strategy review and approval
• Material incident notifications (immediate)
• Risk appetite setting
• CISO appointment and performance reviews

────────────────────────────────────────────

MANAGEMENT RESPONSIBILITIES

Chief Executive Officer (CEO):
• Ultimate accountability for cybersecurity risk
• Ensure adequate resources for security program
• Support CISO and security initiatives
• Report material matters to Board promptly

Chief Information Security Officer (CISO):
• Develop and implement cybersecurity strategy
• Present quarterly reports to Board/Committee
• Notify Board of material incidents within [24 hours]
• Recommend investments and resource needs
• Coordinate with external advisors and auditors

Reporting Line: CISO reports to [CEO / CIO / CTO / Board]
Note: Direct or dotted-line reporting to CEO recommended for independence

Other C-Suite:
• CFO: Budget approval and insurance oversight
• General Counsel: Legal/regulatory compliance
• CIO/CTO: Technology infrastructure security
• COO: Business continuity planning

────────────────────────────────────────────

BOARD MEMBER CYBERSECURITY EXPERTISE

Current State Assessment:
Number of board members with cyber expertise: [X of Y]
Target: At least [1-2] members with relevant background

Desired Expertise:
□ Technology leadership experience (CIO, CTO, CISO)
□ Cybersecurity industry experience
□ Risk management background
□ Regulatory/compliance expertise
□ Incident response experience

Board Education & Training:
All board members will receive:
• Annual cybersecurity training (minimum 2 hours)
• Quarterly threat landscape updates
• Access to external cybersecurity advisors
• Industry conference/workshop attendance (optional)

Topics Covered:
• Cyber threat landscape and trends
• Company-specific risks and controls
• Regulatory requirements and compliance
• Incident response and crisis management
• Cyber insurance and risk transfer

Onboarding:
New board members receive:
• Cybersecurity briefing within first 30 days
• Access to prior Board security presentations
• Introduction to CISO and security leadership
• Overview of current security posture and risks

────────────────────────────────────────────

MEETING CADENCE & AGENDA

Quarterly Committee Meetings (90 minutes):
1. Executive Summary (10 min)
   • Current security posture rating
   • Top 3 risks requiring attention
   • Recent incidents or near-misses

2. Metrics & KPIs Review (15 min)
   • Operational metrics (MTTD, MTTR, incidents)
   • Risk metrics (vulnerabilities, compliance)
   • Financial metrics (budget utilization, ROI)

3. Strategic Initiatives Update (20 min)
   • Progress on approved projects
   • New technology or tool deployments
   • Organizational changes or hiring

4. Compliance & Regulatory (15 min)
   • Audit findings and remediation
   • Regulatory changes and impact
   • Insurance renewals and claims

5. Threat Landscape (15 min)
   • Industry-specific threats
   • Emerging attack techniques
   • Competitor incidents and lessons

6. Deep Dive Topic (20 min)
   • Rotating focus: Supply chain, Cloud, AI, etc.
   • External expert presentations (as needed)

7. Executive Session (10 min)
   • Board-only discussion without management
   • CISO performance and concerns

Annual Full Board Meeting (2-3 hours):
• Comprehensive security strategy review
• Annual risk assessment results
• Budget and resource planning
• CISO performance evaluation
• Board self-assessment on cyber oversight

Ad Hoc Meetings:
Called immediately for:
• Material security incidents
• Regulatory investigations or actions
• Significant vendor/supply chain breaches
• Major security investment decisions

────────────────────────────────────────────

INFORMATION REQUIREMENTS

Quarterly Board Report Must Include:
□ Executive summary (1 page)
□ Security posture dashboard
□ Top 5 risks with mitigation status
□ Incident summary and trends
□ Compliance status and audit findings
□ Budget vs. actuals
□ Upcoming initiatives and decisions needed

Format: Executive-friendly, minimal jargon, visual dashboards preferred

Access Between Meetings:
• Board portal with current security dashboards
• Direct CISO contact information
• Escalation procedures for urgent matters
• Access to external advisors/consultants

────────────────────────────────────────────

PERFORMANCE METRICS

Board Oversight Effectiveness:
• Frequency of cybersecurity discussions
• Board member training completion rate
• Time to Board notification for material incidents
• Board-directed security investments approved
• External audit/assessment findings addressed

Questions Board Should Be Able to Answer:
1. What are our top 3 cybersecurity risks?
2. How do we compare to industry peers?
3. Are we compliant with relevant regulations?
4. Do we have adequate budget and resources?
5. What is our incident response plan?
6. What cyber insurance coverage do we have?
7. How is our CISO performing?
8. What major security investments are planned?

────────────────────────────────────────────

EXTERNAL ADVISORS & RESOURCES

The Board may engage:
• External cybersecurity consultants
• Incident response retainer firms
• Legal counsel specializing in cyber law
• Cyber insurance brokers
• Industry benchmarking services

Budget Allocation:
Annual budget for external advisory: [$X]

Recommended Resources:
• NACD Cyber-Risk Oversight Handbook
• NIST Cybersecurity Framework
• SEC Cybersecurity Disclosure Guidance
• Industry-specific frameworks (HITRUST, PCI-DSS, etc.)

Peer Networks:
• Board member participation in cyber governance forums
• Information sharing with peer company boards
• Industry association working groups

────────────────────────────────────────────

INCIDENT ESCALATION & COMMUNICATION

Material Incident Definition:
An incident is considered material if it meets any of:
□ Financial impact exceeds [$X] or [Y%] of quarterly revenue
□ Customer PII for >[Z] individuals compromised
□ Critical systems down >[W] hours
□ Regulatory reporting required (SEC 8-K, breach laws)
□ Significant media attention or reputational impact
□ Board notification required by cyber insurance

Notification Timeline:
• CISO to CEO: Immediate upon discovery
• CEO to Board Chair/Committee Chair: Within [2-4] hours
• Emergency Board meeting: Within [24] hours if needed
• Written report: Within [48] hours
• Regular updates: [Daily/every 2 days] until resolved

Communication Protocols:
• Use secure channels (encrypted email, secure portal)
• Board members receive identical information
• External communications approved by Board
• SEC filing timeline tracked and managed

────────────────────────────────────────────

CHARTER REVIEW & UPDATES

This Charter will be:
• Reviewed annually by the [Committee Name]
• Updated as regulations or threats evolve
• Approved by full Board of Directors
• Communicated to all relevant stakeholders

Effectiveness Assessment:
Annual self-assessment questionnaire for Board members:
• Do I understand our cybersecurity risks?
• Am I confident in our security program?
• Do I receive adequate information?
• Are meetings productive and informative?
• What additional support or training do I need?

────────────────────────────────────────────

ACKNOWLEDGMENT & APPROVAL

This Board Cybersecurity Oversight Charter was:

Reviewed and Recommended by: [Committee Name]
Date: [Date]

Approved by: Board of Directors, [Company Name]
Date: [Date]

Board Chair Signature: _________________________
Date: _____________

Audit/Risk Committee Chair Signature: _________________________
Date: _____________

Next Review Date: [Date - 12 months from approval]

────────────────────────────────────────────

APPENDIX A: BOARD CYBER EDUCATION TOPICS

Year 1 Curriculum:
Q1: Cyber Threat Landscape & Our Industry
Q2: Understanding Our Security Controls
Q3: Incident Response & Crisis Management
Q4: Regulatory Compliance (SEC, GDPR, etc.)

Year 2 Curriculum:
Q1: Supply Chain & Third-Party Risk
Q2: Cloud Security & Data Protection
Q3: AI/ML Risks & Opportunities
Q4: Cyber Insurance & Risk Transfer

APPENDIX B: SAMPLE BOARD QUESTIONS

Strategic:
• How does our cybersecurity strategy align with business goals?
• What competitive advantage does our security posture provide?
• Are we investing appropriately compared to peers?

Operational:
• How quickly can we detect and respond to attacks?
• What percentage of our systems are fully protected?
• Do we have the right talent and tools?

Risk:
• What keeps the CISO up at night?
• What is our worst-case scenario?
• Are we prepared for a ransomware attack?

Compliance:
• What regulations apply to us?
• Have we had any violations or findings?
• When is our next audit?

APPENDIX C: RED FLAGS FOR BOARD ATTENTION

Warning Signs:
□ CISO turnover or open position >90 days
□ Significant budget cuts to security program
□ Increasing number of security incidents
□ Delayed patching or vulnerability remediation
□ Low employee security training completion
□ Cyber insurance policy non-renewal or price spike
□ Regulatory inquiry or investigation
□ Customer security questionnaire failures
□ Competitor major breach in same industry
□ Board member confusion about cyber risks

Prepared by: [CISO Name / General Counsel]
Date: [Date]`,

    cisoPerformance: `CISO PERFORMANCE REVIEW & EVALUATION
Board Assessment Template

EVALUATION PERIOD: [Start Date] to [End Date]
CISO NAME: [Name]
EVALUATOR: [CEO / Board Committee Chair]
REVIEW DATE: [Date]

────────────────────────────────────────────

EXECUTIVE SUMMARY

Overall Performance Rating: [Exceeds / Meets / Needs Improvement / Unsatisfactory]

Key Strengths:
• [Strength 1]
• [Strength 2]
• [Strength 3]

Areas for Development:
• [Area 1]
• [Area 2]
• [Area 3]

Compensation Recommendation: [Increase / Maintain / Decrease]
Proposed Adjustment: [X%] or [$Y]

────────────────────────────────────────────

SECTION 1: STRATEGIC LEADERSHIP (Weight: 25%)

1.1 Security Strategy & Vision
Rating: [1-5, where 5 = Exceptional]

Evaluation Criteria:
□ Developed comprehensive cybersecurity strategy aligned with business goals
□ Articulated clear vision for security program evolution
□ Anticipated future threats and prepared organization accordingly
□ Balanced security requirements with business enablement

Comments:
[Evaluator notes on strategic leadership performance]

1.2 Board & Executive Communication
Rating: [1-5]

Evaluation Criteria:
□ Presented clear, non-technical reports to Board
□ Proactively informed Board of material risks and incidents
□ Built trust and credibility with Board members
□ Translated technical issues into business impact

Comments:
[Evaluator notes]

1.3 Business Alignment
Rating: [1-5]

Evaluation Criteria:
□ Security program enables rather than impedes business objectives
□ Understands business priorities and customer needs
□ Collaborates effectively with business unit leaders
□ Security investments show clear ROI

Comments:
[Evaluator notes]

STRATEGIC LEADERSHIP SCORE: [Average] / 5

────────────────────────────────────────────

SECTION 2: RISK MANAGEMENT (Weight: 30%)

2.1 Risk Assessment & Prioritization
Rating: [1-5]

Evaluation Criteria:
□ Conducted comprehensive enterprise risk assessments
□ Identified and prioritized top cybersecurity risks
□ Quantified risks in business/financial terms
□ Maintained current risk register and heat maps

Key Metrics:
• Risk assessments completed: [X] (Target: [Y])
• Critical risks mitigated: [X%]
• Average risk remediation time: [X days]

Comments:
[Evaluator notes]

2.2 Incident Management
Rating: [1-5]

Evaluation Criteria:
□ No material security incidents, or incidents well-managed
□ Rapid detection and response to security events
□ Effective coordination during crisis situations
□ Post-incident analysis and lessons learned implemented

Key Metrics:
• Mean Time to Detect (MTTD): [X hours] (Target: <24)
• Mean Time to Respond (MTTR): [X hours] (Target: <48)
• Material incidents: [X]
• Customer/revenue impact: [$X]

Comments:
[Evaluator notes]

2.3 Third-Party & Supply Chain Risk
Rating: [1-5]

Evaluation Criteria:
□ Assessed and managed vendor cybersecurity risks
□ No significant vendor-related incidents
□ Vendor risk program mature and effective
□ Supply chain security incorporated into procurement

Key Metrics:
• Critical vendors assessed: [X of Y] ([Z%])
• High-risk vendors remediated: [X of Y]
• Vendor incidents: [X]

Comments:
[Evaluator notes]

RISK MANAGEMENT SCORE: [Average] / 5

────────────────────────────────────────────

SECTION 3: OPERATIONAL EXCELLENCE (Weight: 20%)

3.1 Security Operations
Rating: [1-5]

Evaluation Criteria:
□ Effective 24/7 security monitoring and operations
□ Timely vulnerability management and patching
□ Strong identity and access management
□ Reliable security tool deployment and management

Key Metrics:
• System uptime: [X%] (Target: >99.5%)
• Patch compliance: [X%] (Target: >95%)
• Critical vulnerabilities open: [X] (Target: <5)
• False positive rate: [X%] (Target: <10%)

Comments:
[Evaluator notes]

3.2 Program Maturity
Rating: [1-5]

Evaluation Criteria:
□ Progressed security program maturity level
□ Achieved relevant certifications (SOC 2, ISO 27001, etc.)
□ Passed external audits with minimal findings
□ Benchmarked favorably against industry peers

Key Metrics:
• Maturity level: [Level X of 5]
• Certifications achieved: [List]
• Audit findings: [X critical, Y high, Z medium]
• Industry percentile: [Xth percentile]

Comments:
[Evaluator notes]

3.3 Project Execution
Rating: [1-5]

Evaluation Criteria:
□ Completed approved security initiatives on time/budget
□ Delivered measurable value from investments
□ Managed vendors and consultants effectively
□ Minimal project delays or cost overruns

Key Metrics:
• Projects completed: [X of Y approved]
• On-time delivery: [X%]
• Budget variance: [+/-X%]
• Realized ROI: [$X saved / risks reduced]

Comments:
[Evaluator notes]

OPERATIONAL EXCELLENCE SCORE: [Average] / 5

────────────────────────────────────────────

SECTION 4: COMPLIANCE & GOVERNANCE (Weight: 15%)

4.1 Regulatory Compliance
Rating: [1-5]

Evaluation Criteria:
□ Maintained compliance with all applicable regulations
□ No regulatory violations or fines
□ Timely SEC disclosures (if applicable)
□ Proactive monitoring of regulatory changes

Key Metrics:
• Compliance status: [100% / X% compliant]
• Regulatory audits passed: [X of Y]
• Fines/penalties: [$X] (Target: $0)
• SEC 8-K filings: [X] (all timely: Y/N)

Comments:
[Evaluator notes]

4.2 Policy & Governance
Rating: [1-5]

Evaluation Criteria:
□ Maintained current and comprehensive security policies
□ Governance framework aligned with industry standards
□ Regular policy reviews and updates
□ Effective policy communication and enforcement

Key Metrics:
• Policies updated: [X of Y due]
• Employee acknowledgment rate: [X%]
• Policy violations: [X] (trend: up/down)

Comments:
[Evaluator notes]

4.3 Cyber Insurance & Risk Transfer
Rating: [1-5]

Evaluation Criteria:
□ Maintained adequate cyber insurance coverage
□ Met all insurance policy requirements
□ Optimized premiums through risk reduction
□ Effective claims management (if applicable)

Key Metrics:
• Coverage amount: [$X million]
• Premium trend: [Up/Down X%]
• Claims filed: [X]
• Claims recovered: [$X]

Comments:
[Evaluator notes]

COMPLIANCE & GOVERNANCE SCORE: [Average] / 5

────────────────────────────────────────────

SECTION 5: TEAM LEADERSHIP & CULTURE (Weight: 10%)

5.1 Team Development
Rating: [1-5]

Evaluation Criteria:
□ Built high-performing security team
□ Low turnover and high employee satisfaction
□ Developed talent and succession plans
□ Fostered inclusive and collaborative culture

Key Metrics:
• Team size: [X FTEs] (vs target: [Y])
• Turnover rate: [X%] (Industry avg: 18%)
• Open positions filled within: [X days]
• Employee satisfaction: [X/10]

Comments:
[Evaluator notes]

5.2 Security Awareness & Training
Rating: [1-5]

Evaluation Criteria:
□ Effective company-wide security awareness program
□ High training completion and engagement rates
□ Measurable improvement in security behaviors
□ Creative and engaging training delivery

Key Metrics:
• Training completion: [X%] (Target: 100%)
• Phishing simulation click rate: [X%] (Target: <5%)
• Reported phishing attempts: [X] (trend: up is good)
• Security culture score: [X/10]

Comments:
[Evaluator notes]

5.3 Cross-Functional Collaboration
Rating: [1-5]

Evaluation Criteria:
□ Strong working relationships across organization
□ Effective collaboration with IT, Legal, HR, Finance
□ Security integrated into business processes
□ Viewed as enabler, not blocker

Comments:
[Evaluator notes]

TEAM LEADERSHIP SCORE: [Average] / 5

────────────────────────────────────────────

OVERALL PERFORMANCE SUMMARY

Category Scores:
• Strategic Leadership (25%): [X.X / 5]
• Risk Management (30%): [X.X / 5]
• Operational Excellence (20%): [X.X / 5]
• Compliance & Governance (15%): [X.X / 5]
• Team Leadership (10%): [X.X / 5]

WEIGHTED OVERALL SCORE: [X.X / 5]

Performance Rating:
[ ] 4.5-5.0: Exceptional - Far exceeds expectations
[ ] 3.5-4.4: Exceeds Expectations - Consistently strong performance
[ ] 2.5-3.4: Meets Expectations - Solid, competent performance
[ ] 1.5-2.4: Needs Improvement - Performance gaps requiring attention
[ ] 1.0-1.4: Unsatisfactory - Immediate improvement required

────────────────────────────────────────────

GOALS FOR NEXT REVIEW PERIOD

Strategic Goals:
1. [Specific strategic objective with measurable outcome]
2. [Specific strategic objective with measurable outcome]
3. [Specific strategic objective with measurable outcome]

Operational Goals:
1. [Specific operational objective with measurable outcome]
2. [Specific operational objective with measurable outcome]

Professional Development:
1. [Specific development area or training]
2. [Specific development area or training]

────────────────────────────────────────────

COMPENSATION RECOMMENDATION

Current Base Salary: [$X]
Proposed Adjustment: [Y%] or [$Z]
New Base Salary: [$X + Z]

Rationale:
[Explanation of compensation recommendation based on performance,
market benchmarks, and company budget]

Bonus/Incentive Compensation:
• Target bonus: [X% of base]
• Actual bonus recommended: [Y% of base] (based on performance)
• Amount: [$Z]

Equity/Long-Term Incentives:
• Stock options/RSUs: [X shares/units]
• Vesting schedule: [Terms]

Total Cash Compensation: [$X]
Total Target Compensation: [$Y]

Market Benchmarking:
• Industry median for similar role: [$X]
• Company size peer group: [$Y]
• Our position: [Below/At/Above median by Z%]

────────────────────────────────────────────

DEVELOPMENT PLAN

Areas for Growth:
1. [Specific area]
   Action: [Training, mentorship, project assignment]
   Timeline: [Date]

2. [Specific area]
   Action: [Training, mentorship, project assignment]
   Timeline: [Date]

Resources Provided:
• Executive coaching: [Y/N]
• Industry certifications: [List]
• Conference attendance: [List]
• Peer networking: [Groups/Forums]

────────────────────────────────────────────

EVALUATOR SIGNATURES

This performance review has been discussed with the employee.

CISO Signature: _________________________ Date: _________

Comments/Acknowledgment:
[CISO may provide written response or comments]

Evaluator Signature: _________________________ Date: _________
[CEO / Committee Chair Name]

Board Committee Approval: _________________________ Date: _________
[Audit/Risk Committee Chair]

HR Acknowledged: _________________________ Date: _________

────────────────────────────────────────────

APPENDIX: CISO COMPENSATION BENCHMARKING

Industry Benchmarks (Annual Base Salary):
• Startup (<100 employees): $150K-$220K
• Small Business (100-500): $180K-$280K
• Mid-Market (500-2000): $220K-$350K
• Enterprise (2000-10000): $280K-$450K
• Large Enterprise (10000+): $350K-$600K+

Total Compensation (Base + Bonus + Equity):
• Add 20-40% for target bonus
• Add equity value (typically 0.1-0.5% for startups)

Geographic Adjustments:
• San Francisco/NYC: +20-30%
• Major Tech Hubs: +10-15%
• Secondary Markets: Baseline
• Rural/Remote: -5-10%

Industry Premiums:
• Financial Services: +15-25%
• Healthcare: +10-20%
• Technology: +10-15%
• Retail/Hospitality: Baseline

Factors Increasing Compensation:
• Public company (SEC reporting): +10-20%
• Recent major incident: +5-15% (retention)
• High-growth company: +10-20%
• Extensive compliance requirements: +10-15%
• Large security team (20+ people): +15-25%

Sources:
• Dice Tech Salary Report
• Robert Half Technology Salary Guide
• ISC2 Cybersecurity Workforce Study
• Gartner Executive Compensation Survey`,

    hiringCISO: `HOW TO HIRE A CISO
Board Decision Guide: Full-Time, Fractional, or MSSP

EXECUTIVE SUMMARY

This guide helps boards and executives determine the right Chief Information
Security Officer (CISO) model for their organization and provides step-by-step
hiring guidance.

THREE CISO MODELS COMPARED:

1. FULL-TIME CISO (Traditional FTE)
   Best for: Companies with >500 employees, complex security needs, or
   high-risk industries

2. FRACTIONAL/VIRTUAL CISO (vCISO)
   Best for: SMBs, startups, or companies building security programs

3. MSSP-PROVIDED CISO
   Best for: Cost-conscious organizations needing compliance + operations

────────────────────────────────────────────

DECISION FRAMEWORK: WHICH MODEL IS RIGHT?

Answer these questions to determine your best fit:

Company Size:
□ <100 employees → Fractional or MSSP
□ 100-500 employees → Fractional or Full-Time
□ 500-2000 employees → Full-Time (likely)
□ 2000+ employees → Full-Time (definitely)

Security Maturity:
□ No security program → Fractional (to build) → Full-Time (to run)
□ Basic security in place → Fractional or Full-Time
□ Mature program → Full-Time + team

Budget:
□ <$150K/year → MSSP
□ $150K-$250K/year → Fractional
□ $250K-$400K/year → Full-Time (mid-market)
□ $400K+ available → Full-Time (senior)

Compliance Requirements:
□ Minimal (best practices only) → Any model
□ Moderate (SOC 2, ISO) → Fractional or Full-Time
□ Heavy (HIPAA, PCI-DSS, SEC) → Full-Time
□ Multiple frameworks + audit frequency → Full-Time + team

Risk Profile:
□ Low risk (B2B SaaS, no PII) → Fractional or MSSP
□ Medium risk (customer data, some PII) → Fractional or Full-Time
□ High risk (Financial, Healthcare, Critical Infrastructure) → Full-Time
□ Very high risk (Recent breach, regulatory scrutiny) → Full-Time + team

Incident History:
□ No major incidents → Any model works
□ Minor incidents handled → Any model works
□ Major breach in last 2 years → Full-Time
□ Active investigation/litigation → Full-Time + external counsel

Growth Stage:
□ Early stage startup → Fractional
□ Growth stage ($10M-$50M revenue) → Fractional → Full-Time
□ Scale-up ($50M-$200M) → Full-Time
□ Enterprise ($200M+) → Full-Time + CISO team

────────────────────────────────────────────

OPTION 1: FULL-TIME CISO (FTE)

PROS:
✓ Dedicated focus on your organization
✓ Deep knowledge of your business and culture
✓ Available for urgent matters 24/7
✓ Builds and leads security team
✓ Face of security to board and customers
✓ Long-term strategic thinking
✓ Team development and succession planning

CONS:
✗ Highest cost ($250K-$600K+ total comp)
✗ Recruiting challenge (limited talent pool)
✗ Long hiring process (3-6 months typical)
✗ Risk of single point of failure
✗ May lack breadth of experience
✗ Potential for burnout

TOTAL COST:
• Base salary: $200K-$450K
• Bonus (20-40%): $40K-$180K
• Equity: Variable
• Benefits: $30K-$50K
• Recruiter fee: $50K-$100K (one-time)
• Total Year 1: $350K-$700K+

WHEN TO HIRE FULL-TIME:
□ Company has >500 employees
□ Revenue >$50M annually
□ Security team of 3+ people to manage
□ Board requires dedicated CISO
□ Regulatory requirements demand it
□ Customer contracts require named CISO
□ Previous CISO departed (replacement needed)
□ Building or running SOC

────────────────────────────────────────────

OPTION 2: FRACTIONAL/VIRTUAL CISO (vCISO)

PROS:
✓ Lower cost ($8K-$25K/month)
✓ Flexible engagement (scale up/down)
✓ Immediate start (days vs months)
✓ Broad experience across companies
✓ No benefits or equity needed
✓ Easy to change if not working
✓ Can upgrade to FTE later
✓ Often brings network/resources

CONS:
✗ Part-time availability (typically 20-40 hrs/month)
✗ Managing multiple clients
✗ May lack deep company knowledge
✗ Less team development/mentoring
✗ Potential conflicts of interest
✗ Limited emergency availability
✗ Relationship feels transactional

TYPICAL ENGAGEMENT:
• Time commitment: 20-80 hours/month
• Duration: 6-24 month contracts
• Deliverables: Strategy, policies, assessments, board reports
• Team support: Often includes analysts/consultants

COST STRUCTURE:
• Retainer model: $8K-$25K/month
• Hourly: $200-$400/hour (not recommended)
• Project-based: $15K-$50K per deliverable
• Typical annual: $100K-$300K

WHEN TO USE FRACTIONAL:
□ Company 50-500 employees
□ Building security program from scratch
□ Post-breach remediation project
□ Interim coverage (CISO departed)
□ Budget constraints
□ Testing waters before FTE commitment
□ Compliance deadline approaching
□ Board requires CISO title but can't justify FTE

SERVICE PROVIDERS:
• Specialized vCISO firms (CyberVista, Pivot Point, etc.)
• Consulting firms (Big 4, boutiques)
• Independent fractional CISOs
• Managed Security Service Providers (MSSPs)

────────────────────────────────────────────

OPTION 3: MSSP-PROVIDED CISO

PROS:
✓ Lowest cost option
✓ Bundled with security operations
✓ 24/7 SOC + CISO function
✓ Turnkey solution
✓ Scalable team behind CISO
✓ Technology included
✓ Fast deployment

CONS:
✗ CISO may be spread thin across many clients
✗ Cookie-cutter approach
✗ Less strategic, more compliance-focused
✗ May push their tools/services
✗ Limited customization
✗ Vendor lock-in risk
✗ Quality varies widely by provider

TYPICAL PACKAGE:
• Dedicated vCISO: 10-30 hrs/month
• SOC services: 24/7 monitoring
• Compliance support: Policies, audits
• Incident response: Included
• Tools: SIEM, EDR, etc.
• Cost: $5K-$15K/month all-in

WHEN TO USE MSSP MODEL:
□ <200 employees
□ Need SOC + leadership together
□ Limited internal IT capability
□ Compliance-driven (need SOC 2, ISO fast)
□ Budget <$150K/year total for security
□ No capacity to manage vendors
□ Want single-throat-to-choke

TOP MSSP PROVIDERS:
• Arctic Wolf
• Cybrary
• ReliaQuest
• Secureworks
• Trustwave
• (Many regional/boutique options)

────────────────────────────────────────────

HIRING PROCESS: FULL-TIME CISO

PHASE 1: DEFINE REQUIREMENTS (2-4 weeks)

Job Description Elements:
• Title: CISO, VP Security, Head of Information Security
• Reporting: CEO (ideal), CIO/CTO (acceptable), Board (rare)
• Responsibilities: Strategy, team, budget, board reporting, compliance
• Team size: Current + planned growth
• Budget: Current spend + proposed increases
• Required: Certifications (CISSP, CISM), years experience, industry
• Preferred: Company size match, framework experience, leadership

Compensation Package:
• Base salary: Research market rates (see benchmarking section)
• Bonus structure: 20-40% target
• Equity: 0.1-0.5% for startups, RSUs for public companies
• Benefits: Standard + professional development budget
• Perks: Conference attendance, certification renewals

Success Metrics (First 90 Days):
1. Complete security assessment and present findings to board
2. Develop 12-month security roadmap
3. Build relationships with key stakeholders
4. Hire/plan for team growth (if applicable)
5. Quick wins to demonstrate value

PHASE 2: SOURCING CANDIDATES (4-8 weeks)

Recruiting Channels:
• Executive recruiters (recommended for $300K+ roles)
  - Security-specialized: CyberSN, Optiv, ClearanceJobs
  - Generalist executive: Korn Ferry, Spencer Stuart
  - Cost: 25-35% of first-year salary

• Job boards:
  - LinkedIn (post + recruiter seat)
  - Indeed (sponsored listing)
  - CyberSecJobs, ISC2 Career Center
  - AngelList (startups)

• Networking:
  - CISO peer groups (Evanta, ISSA chapters)
  - Industry conferences (RSA, Black Hat)
  - Board member referrals
  - Internal employee referrals

• Fractional-to-FTE conversion:
  - Hire fractional CISO, evaluate, offer FTE if good fit

Target Candidate Profile:
• 10-15+ years security experience
• 5+ years leadership experience
• Prior CISO or equivalent role
• Industry experience (nice-to-have)
• Company size match (within 2x employee count)
• Technical depth + business acumen
• Strong communicator (board presentations)
• Certifications: CISSP, CISM, CISA (preferred)

Red Flags:
✗ Job hopping (CISO <2 years at multiple companies)
✗ Overly technical, can't communicate to business
✗ No team leadership experience
✗ Combative or defensive in interviews
✗ Doesn't ask about budget or resources
✗ References won't speak enthusiastically
✗ Overemphasis on certifications vs. results

PHASE 3: INTERVIEW PROCESS (3-6 weeks)

Interview Loop:
1. Phone Screen (30 min): HR/Recruiter
   - Background verification
   - Compensation alignment
   - Logistics and availability

2. Hiring Manager Interview (60 min): CEO or CTO
   - Strategic thinking
   - Leadership philosophy
   - Company fit

3. Technical Interview (90 min): CTO + Senior Engineers
   - Security architecture knowledge
   - Incident response experience
   - Tool and framework familiarity

4. Stakeholder Interviews (4-6 x 45 min each):
   - General Counsel: Compliance and legal
   - CFO: Budget and business case skills
   - VP Engineering: Collaboration and enablement
   - Head of HR: Culture and team building
   - Customer Success: Customer-facing skills

5. Board Interview (60-90 min): Board Member(s)
   - Communication and presentation
   - Risk articulation
   - Strategic vision

6. Executive Presentation (Optional):
   - 30-day plan presentation to leadership
   - Security assessment approach
   - Q&A with exec team

Key Interview Questions:

Strategic:
• "How do you align security strategy with business objectives?"
• "Describe a time you had to make a risk-based decision under pressure."
• "How do you prioritize security investments with limited budget?"

Leadership:
• "Tell me about building a security team from scratch."
• "How do you handle conflict between security and business needs?"
• "Describe your approach to developing junior security talent."

Technical:
• "Walk me through your incident response process."
• "How do you approach threat modeling for [our type of product]?"
• "What security frameworks have you implemented and why?"

Business:
• "How do you communicate technical risks to the board?"
• "Describe a time you successfully obtained budget for a security initiative."
• "How do you measure security program success?"

PHASE 4: REFERENCE CHECKS (1 week)

Ask References:
• How would you rate [Candidate]'s performance? (1-10 scale)
• What were their biggest accomplishments?
• What were their weaknesses or areas for improvement?
• How did they handle [specific situation]?
• Would you hire them again?
• Any concerns we should know about?

Check:
• 3-5 professional references
• At least 2 from board/C-suite level
• At least 1 from direct report
• Optional: Peer references

Background Check:
• Criminal background
• Employment verification
• Education verification (degree mills exist!)
• Credit check (if handling budget/vendors)
• Social media review (public posts)

PHASE 5: OFFER & ONBOARDING (2-4 weeks)

Offer Package:
• Written offer letter with all terms
• Salary, bonus structure, equity details
• Benefits summary
• Start date and relocation (if applicable)
• Contingencies: Background check, references

Negotiation Tips:
• Be prepared for counter-offer from current employer
• Flexibility on equity vs cash
• Professional development budget
• Conference attendance
• Certification renewal
• Title (some want SVP vs VP)

Onboarding Plan (First 90 Days):

Week 1-2: Learning
• Meet all stakeholders (30+ meetings)
• Access to systems and documentation
• Review previous security assessments
• Understand current budget and spending

Week 3-4: Assessment
• Conduct security posture assessment
• Identify quick wins and critical gaps
• Interview security team (if exists)
• Review vendor contracts and tools

Week 5-8: Planning
• Draft 90-day, 6-month, 12-month roadmap
• Prioritize initiatives
• Build budget proposal
• Start policy/framework updates

Week 9-12: Execution & Board Presentation
• Implement quick wins
• Present assessment and plan to board
• Hire critical roles (if approved)
• Establish board reporting cadence

────────────────────────────────────────────

HIRING PROCESS: FRACTIONAL CISO

PHASE 1: DEFINE SCOPE (1-2 weeks)

Determine Needs:
• Hours per month: 20 (light), 40 (standard), 80 (heavy)
• Duration: 6 months (project), 12 months (ongoing), 24+ months (partnership)
• Deliverables: Strategy, policies, assessments, board reports, team building
• On-site requirements: Monthly, quarterly, or fully remote
• Industry expertise: Required or nice-to-have

PHASE 2: EVALUATE PROVIDERS (2-3 weeks)

Evaluation Criteria:
□ Relevant industry experience
□ Client references in similar stage/size
□ Clear scope of work and deliverables
□ Transparent pricing
□ Contract flexibility (month-to-month vs annual)
□ Team depth (solo practitioner vs firm)
□ Technology partnerships (vendor-neutral vs biased)
□ Availability and response times

Request from Providers:
• Sample deliverables (redacted client work)
• 3 client references
• Credentials of assigned vCISO
• Service level agreement (SLA)
• Statement of work (SOW) template
• Pricing and contract terms

Interview Questions:
• "How many clients do you currently serve?"
• "What's your average client tenure?"
• "How do you handle emergency situations?"
• "Do you have any technology vendor partnerships?" (bias check)
• "Can we switch to a different vCISO if needed?"
• "What happens when our vCISO is on vacation?"

PHASE 3: PILOT ENGAGEMENT (1-3 months)

Start Small:
• 3-month pilot engagement
• Defined deliverable (e.g., risk assessment + roadmap)
• Monthly check-ins with CEO/Board
• Evaluate fit before longer commitment

Success Metrics:
• Quality of deliverables
• Responsiveness and communication
• Cultural fit with organization
• Value delivered vs cost
• Would we extend the engagement?

────────────────────────────────────────────

TRANSITION PLANNING

Fractional → Full-Time:
Many companies start with fractional and transition to FTE when:
• Company reaches 300-500 employees
• Security team grows to 3+ people
• Budget supports FTE salary
• Workload exceeds fractional capacity
• Board wants dedicated CISO

Transition Approach:
1. Give fractional CISO option to convert to FTE (if good fit)
2. If they decline or not right fit, use them to help recruit
3. Overlap 1-2 months for knowledge transfer
4. Retain as advisor for 90 days post-transition

Full-Time → Fractional (Downgrade):
Rare, but happens when:
• Budget cuts require it
• Company pivots or downsizes
• CISO leaves and can't afford replacement

Interim Coverage (Unexpected Departure):
• Fractional CISO immediately while recruiting FTE
• Internal IT leader temporarily promoted
• Consulting firm interim coverage
• Board member with security background steps in

────────────────────────────────────────────

COST COMPARISON SUMMARY

Scenario: 300-person company, moderate security needs

FULL-TIME CISO:
• Base salary: $280K
• Bonus (30%): $84K
• Benefits: $40K
• Equity: $50K (estimated annual value)
• Recruiter: $85K (one-time)
• Year 1 Total: $539K
• Ongoing Annual: $454K

FRACTIONAL CISO:
• Retainer: $15K/month
• Annual: $180K
• Team support: $30K (ad-hoc projects)
• Total: $210K/year
• Savings vs FTE: $244K (54%)

MSSP MODEL:
• All-in monthly: $10K
• Annual: $120K
• Includes: vCISO + SOC + tools
• Savings vs FTE: $334K (74%)

────────────────────────────────────────────

RECOMMENDATION FRAMEWORK

START HERE:

If you're: | Choose:
<100 employees, <$10M revenue | MSSP or Fractional
100-300 employees, $10M-$50M revenue | Fractional
300-500 employees, $50M-$100M revenue | Fractional → FTE
500-2000 employees, $100M-$500M | Full-Time
2000+ employees, $500M+ revenue | Full-Time + Team

INDUSTRY MODIFIERS:

Add Full-Time preference if:
• Financial services (regulatory heavy)
• Healthcare (HIPAA + patient safety)
• Critical infrastructure
• Public company (SEC reporting)
• Recent major breach
• Heavy M&A activity

BUDGET REALITY CHECK:

If total security budget is:
• <$200K → MSSP only option
• $200K-$400K → Fractional + some tools
• $400K-$800K → Fractional or junior FTE + tools
• $800K-$1.5M → Full-Time CISO + small team
• $1.5M-$3M → Full-Time CISO + team + SOC
• $3M+ → Full-Time CISO + mature program

────────────────────────────────────────────

MAKING THE DECISION

Board Questions to Answer:

1. What is our risk appetite and tolerance?
2. What do our regulators/auditors/insurers require?
3. What do our customers expect?
4. Can we attract FTE talent in our location?
5. What is our budget reality?
6. How quickly do we need to move?
7. Is this a permanent or temporary need?
8. What does our growth trajectory look like?

Success Indicators (6 Months After Hiring):

□ Security program roadmap exists and is funded
□ Board receives regular, clear security reports
□ Security incidents detected and resolved faster
□ Compliance audit findings decreased
□ Employee security awareness improved
□ Customer security questionnaires completed smoothly
□ Cyber insurance premiums stable or decreasing
□ Executive team confident in security posture

Prepared by: [Name]
Date: [Date]

For questions or guidance on CISO hiring, contact:
[HR / Board Committee / External Advisor]`
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTemplate(true);
      setTimeout(() => setCopiedTemplate(false), 2000);
    });
  };

  // Templates Tab
  const TemplatesTab = () => {
    const templates = [
      {
        id: 'boardReport',
        title: 'Quarterly Board Report',
        icon: FileText,
        color: 'blue',
        description: 'Comprehensive quarterly cybersecurity report with metrics and risk assessment',
        tags: ['Report', 'Quarterly', 'Executive']
      },
      {
        id: 'incidentResponse',
        title: 'Incident Response Plan',
        icon: AlertTriangle,
        color: 'red',
        description: 'Executive-level incident response communication template',
        tags: ['Incident', 'Emergency', 'Communication']
      },
      {
        id: 'budgetJustification',
        title: 'Budget Justification',
        icon: DollarSign,
        color: 'green',
        description: 'Security investment business case with ROI calculator',
        tags: ['Budget', 'ROI', 'Business Case']
      },
      {
        id: 'metricsKPI',
        title: 'KPI Metrics Dashboard',
        icon: BarChart3,
        color: 'indigo',
        description: 'Board-level cybersecurity metrics and KPI tracking',
        tags: ['KPI', 'Metrics', 'Dashboard']
      },
      {
        id: 'riskRegister',
        title: 'Risk Register',
        icon: Target,
        color: 'orange',
        description: 'Cybersecurity risk register with scoring methodology',
        tags: ['Risk', 'Register', 'Assessment']
      },
      {
        id: 'sec8k',
        title: 'SEC 8-K Incident Disclosure',
        icon: Scale,
        color: 'red',
        description: 'Form 8-K Item 1.05 template for material cybersecurity incidents with materiality assessment checklist and 4-day timeline',
        tags: ['SEC', 'Compliance', 'Disclosure']
      },
      {
        id: 'tabletopExercise',
        title: 'Tabletop Exercise Guide',
        icon: Users,
        color: 'purple',
        description: 'Complete guide to creating and running cybersecurity tabletop exercises for your organization with scenarios and discussion questions',
        tags: ['Training', 'Tabletop', 'Preparedness']
      },
      {
        id: 'annualTraining',
        title: 'Annual Security Awareness Training',
        icon: BookOpen,
        color: 'blue',
        description: 'Year-end comprehensive security awareness training program covering 10 modules from phishing to compliance',
        tags: ['Training', 'Awareness', 'Compliance']
      },
      {
        id: 'boardCharter',
        title: 'Board Cybersecurity Charter',
        icon: Award,
        color: 'purple',
        description: 'Defines board\'s role in cyber oversight, committee structure, and quarterly review cadence',
        tags: ['Governance', 'Charter', 'Board']
      },
      {
        id: 'cisoPerformance',
        title: 'CISO Performance Review',
        icon: TrendingUp,
        color: 'green',
        description: 'Board evaluation criteria with KPIs, strategic vs operational goals, and compensation benchmarking',
        tags: ['Performance', 'Review', 'CISO']
      },
      {
        id: 'hiringCISO',
        title: 'How to Hire a CISO',
        icon: UserPlus,
        color: 'indigo',
        description: 'Decision guide comparing Full-Time, Fractional, and MSSP models with hiring processes and cost analysis',
        tags: ['Hiring', 'CISO', 'Decision Guide']
      }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-white/90 p-6 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Templates & Resources</h2>
          <p className="text-gray-600 mb-6">
            Click "View Template" to see the full content, then copy and customize for your organization. No downloads or database needed - just copy and paste!
          </p>

          <div className="space-y-4">
            {templates.map((template) => {
              const Icon = template.icon;
              return (
                <div key={template.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold mb-2 flex items-center gap-2">
                        <Icon className={`text-${template.color}-600`} size={20} />
                        {template.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {template.description}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {template.tags.map(tag => (
                          <span key={tag} className={`text-xs bg-${template.color}-100 text-${template.color}-700 px-2 py-1 rounded`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveTemplate(template.id)}
                      className={`ml-4 px-4 py-2 bg-gradient-to-r from-${template.color}-500 to-${template.color}-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 whitespace-nowrap`}
                    >
                      <FileText size={16} />
                      View Template
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h3 className="font-bold mb-3">📚 Additional Resources</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <div>
                <strong>NACD Cyber-Risk Oversight Handbook:</strong>
                <span className="text-gray-700 ml-1">Five principles for director oversight of cybersecurity</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <div>
                <strong>NIST Cybersecurity Framework:</strong>
                <span className="text-gray-700 ml-1">Core standards for risk management and governance</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <div>
                <strong>SEC Cybersecurity Disclosure Rules:</strong>
                <span className="text-gray-700 ml-1">Official guidance and compliance requirements</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <div>
                <strong>World Economic Forum Cyber Playbook:</strong>
                <span className="text-gray-700 ml-1">Board-level cyber governance toolkit</span>
              </div>
            </div>
          </div>
        </div>

        {/* Template Modal */}
        {activeTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setActiveTemplate(null)}>
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-2xl font-bold text-gray-800">
                  {templates.find(t => t.id === activeTemplate)?.title}
                </h3>
                <button
                  onClick={() => setActiveTemplate(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg border">
                  {templateData[activeTemplate]}
                </pre>
              </div>

              <div className="flex items-center justify-between p-6 border-t bg-gray-50">
                <p className="text-sm text-gray-600">
                  Copy this template and customize it for your organization
                </p>
                <button
                  onClick={() => copyToClipboard(templateData[activeTemplate])}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  <Copy size={18} />
                  {copiedTemplate ? 'Copied!' : 'Copy Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4">
      <WelcomeModal onNavigate={setActiveTab} />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-3 rounded-lg">
                <Shield className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Board of Security</h1>
                <p className="text-gray-600">Executive Cybersecurity Governance Toolkit</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                CISO Marketplace
              </span>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold">
                MicroSec.Tools
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white/80 backdrop-blur rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 px-4 py-4 font-semibold transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-blue-600 bg-blue-50'
                      : 'text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm md:text-base">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'assessment' && <AssessmentTab />}
          {activeTab === 'reporting' && <ReportingTab />}
          {activeTab === 'metrics' && <MetricsTab />}
          {activeTab === 'structure' && <StructureTab />}
          {activeTab === 'compliance' && <ComplianceTab />}
          {activeTab === 'frameworks' && <GlobalFrameworksTab />}
          {activeTab === 'engagement' && <EngagementTab />}
          {activeTab === 'prep' && <BoardMeetingPrepTab />}
          {activeTab === 'templates' && <TemplatesTab />}
        </div>

        {/* Related Micro Tools Showcase */}
        <div className="mt-8 bg-white/90 rounded-lg shadow-md p-6 border-2 border-indigo-200">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">🔧 Related Cybersecurity Micro Tools</h3>
            <p className="text-gray-600">
              Explore specialized tools from the CISO Marketplace ecosystem to deepen your board's understanding of specific security domains
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Breach Notification Tool */}
            <a 
              href="https://notification.breached.company/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group border-2 border-red-200 rounded-lg p-5 hover:border-red-400 hover:shadow-lg transition-all bg-gradient-to-br from-red-50 to-white"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-red-100 p-2 rounded-lg group-hover:bg-red-200 transition-colors">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg text-gray-800 group-hover:text-red-600 transition-colors">
                    Breach Notification Timeline
                  </h4>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">breached.company</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Interactive tool showing global data breach notification requirements and timelines. Critical for boards understanding compliance obligations across jurisdictions.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">GDPR 72-hour</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">SEC 4-day</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">State laws</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">NIS2</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-red-600 font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Explore Notification Requirements</span>
                <span>→</span>
              </div>
            </a>

            {/* Privacy Rights Tool */}
            <a 
              href="https://privacyrights.compliancehub.wiki/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group border-2 border-blue-200 rounded-lg p-5 hover:border-blue-400 hover:shadow-lg transition-all bg-gradient-to-br from-blue-50 to-white"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                  <Shield className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg text-gray-800 group-hover:text-blue-600 transition-colors">
                    Privacy Rights Explorer
                  </h4>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">compliancehub.wiki</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Comprehensive guide to consumer privacy rights across global regulations. Helps boards understand data subject rights and organizational obligations.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Right to access</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Right to deletion</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Portability</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">CCPA</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Explore Privacy Rights</span>
                <span>→</span>
              </div>
            </a>

            {/* PII Identifier Tool */}
            <a 
              href="https://pii.compliancehub.wiki/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group border-2 border-green-200 rounded-lg p-5 hover:border-green-400 hover:shadow-lg transition-all bg-gradient-to-br from-green-50 to-white"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200 transition-colors">
                  <FileText className="text-green-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg text-gray-800 group-hover:text-green-600 transition-colors">
                    PII Identification Guide
                  </h4>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">compliancehub.wiki</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Educational tool defining Personally Identifiable Information (PII) types and sensitivity levels. Essential for boards overseeing data protection strategies.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Direct identifiers</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Indirect identifiers</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Sensitive PII</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Data classification</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-green-600 font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Explore PII Types</span>
                <span>→</span>
              </div>
            </a>

            {/* Biometric Data Tool */}
            <a 
              href="https://biometric.myprivacy.blog/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group border-2 border-purple-200 rounded-lg p-5 hover:border-purple-400 hover:shadow-lg transition-all bg-gradient-to-br from-purple-50 to-white"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <Users className="text-purple-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg text-gray-800 group-hover:text-purple-600 transition-colors">
                    Biometric Data Regulations
                  </h4>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">myprivacy.blog</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Specialized guide to biometric data regulations and compliance requirements. Critical for boards as organizations adopt facial recognition, fingerprints, and AI systems.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">BIPA</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Facial recognition</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Consent requirements</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">GDPR Article 9</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-purple-600 font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Explore Biometric Rules</span>
                <span>→</span>
              </div>
            </a>
          </div>

          <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200">
            <div className="flex items-start gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Award className="text-indigo-600" size={20} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-1">Why These Tools Matter for Boards</h4>
                <p className="text-sm text-gray-700">
                  These specialized micro tools help board members quickly understand complex compliance domains without becoming technical experts. 
                  Each tool translates regulations into actionable insights, helping boards fulfill their oversight responsibilities and ask the right questions of management.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold mb-2">Part of the CISO Marketplace Ecosystem</h3>
              <p className="text-blue-100">
                Comprehensive cybersecurity tools for modern security leaders
              </p>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://cisomarketplace.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Visit CISO Marketplace
              </a>
              <a 
                href="https://www.microsec.tools" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-400 transition-colors"
              >
                More Micro Tools
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardOfSecurity;